// GraphQL Queries and Mutations for Job Applications, Companies, and Job Seekers

import type { CompanyFormData } from '@components/Companies/types';
import type { EmployersFormData } from '@components/Employers/types';
import type { JobFormData } from '@components/Jobs/types';
import type { JobSeekerFormData } from '@components/JobSeekers/types';
import type { PromoForm } from '@components/PromoCodes/promoCodes';
import type { UserFormData } from '@components/UserDetails';
import { graphQLArrayFormatter } from '@helpers/utils';
import { currentUserDetails } from '@store/accessor';

import { toJobStatusEnum, toJobTypeEnum } from './jobEnums';
import { normalizeSalaryFrequency } from './salary';

/**
 * A string value, quoted and escaped, ready to inline into a mutation.
 *
 * These documents are built by concatenation, so every value dropped between two quote characters
 * is one `"` away from breaking the whole document -- and company names, job titles and addresses
 * are typed by employers, so they arrive from outside. JSON.stringify produces exactly GraphQL's
 * string syntax for quotes, backslashes and newlines.
 *
 * `?? ''` matters as much as the escaping: JSON.stringify(undefined) returns the *value*
 * undefined, which interpolates the bare token `about: undefined` and fails to parse. `about` was
 * the one field here without that guard.
 *
 * Building these with real GraphQL variables would remove the hazard rather than contain it, but
 * that is a rewrite of this file.
 */
const gql = (value: unknown): string => JSON.stringify(value ?? '');

/**
 * Drops blank rows from a free-text key/value list (e.g. a job's internalFilters).
 *
 * Escaping is NOT done here. It used to hand-roll `.replace(/"/g, '\\"')` on both sides, which
 * handled a quote but not a backslash and not a newline, and which now belongs to
 * `graphQLArrayFormatter` -- the thing that actually writes the quote characters. Doing it in
 * both places would escape twice and put literal backslashes in the data.
 */
const sanitizeKeyValues = (
  rows: { key: string; value: string }[] | undefined | null,
): { key: string; value: string }[] =>
  (rows ?? []).filter((r) => r && (r.key?.trim() || r.value?.trim()));

// ------------------- Job Applications Queries -------------------
export const jobApplicantsList = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllJobApplications(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: [${filter}]) {
      content {
        _id,
        status,
        subStatus,
        closedReason,
        ownerId,
        nextAction,
        nextActionAt,
        employerVisible,
        assignedHRs,
        createdAt,
        updatedAt,
        relevancyPercent,
        relevancyStatus,
        jobSeeker {
          _id,
          personalDetails {
            fullName
          }
        },
        job {
          title,
          companyName,
          location,
        }
      }
      totalPages,
      totalElements
    }
  }
`;

/**
 * Richer applications query used by the multi-sheet "Submissions" workbook export.
 * Selects the extra candidate/job fields (phone, city, external flag, notes, feedback,
 * hiringMode) the workbook needs but the on-screen table does not, so the page's own
 * list query stays lean.
 */
export const workbookApplicationsQuery = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllJobApplications(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: [${filter}]) {
      content {
        _id,
        status,
        subStatus,
        closedReason,
        ownerId,
        nextAction,
        nextActionAt,
        assignedHRs,
        createdAt,
        jobId,
        jobSeeker {
          _id,
          isExternal,
          currentSalary,
          personalDetails { fullName, phone, city }
        },
        job {
          title,
          companyName,
          companyId,
          location,
          city,
          hiringMode
        },
        notes { note, date, visibility },
        feedback { rating, comment, date }
      }
      totalPages,
      totalElements
    }
  }
`;

/**
 * Richer jobs query for the workbook: internal filters (gender/religion/skills), salary
 * breakdown, sales owner, number of positions, skills and status per demand.
 */
export const workbookJobsQuery = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllJobs(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: [${filter}]) {
      content {
        _id,
        title,
        companyName,
        companyId,
        location,
        locality,
        city,
        state,
        jobStatus,
        hiringMode,
        industry,
        gender,
        experience,
        yearsOfExperience,
        salary,
        salaryMin,
        salaryMax,
        salaryCurrency,
        salesOwnerName,
        numberOfPositions,
        skillsRequired,
        relevantCandidates { jobSeekerId },
        internalFilters { key, value },
        dataAvailable,
        createdAt
      }
      totalPages,
      totalElements
    }
  }
`;

/** Talent-pool query for the workbook (covers CAD-style pools + external candidates). */
export const workbookTalentPoolQuery = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllJobSeekers(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters:[${filter}]) {
      content {
        _id,
        isExternal,
        currentCompany,
        currentLocation,
        currentSalary,
        experience,
        yearsOfExperience,
        skills,
        createdAt,
        personalDetails { fullName, phone, city, email },
        jobPreferences {
          salaryExpectationMin,
          salaryExpectationMax,
          salaryExpectationCurrency,
          noticePeriod
        }
      }
      totalElements,
      totalPages
    }
  }
`;

export const jobApplicationTracking = (id: string) => `
  query {
    getJobApplicationById(_id: "${id}") {
      _id,
      status,
      subStatus,
      closedReason,
      closedNote,
      ownerId,
      nextAction,
      nextActionAt,
      statusUpdatedAt,
      employerVisible,
      rtrRequestedAt,
      rtrConfirmedAt,
      rtrRevokedAt,
      rtrOverriddenAt,
      jobId,
      jobSeekerId,
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
      source,
      # 0 to 100, null when either side has no profile vector yet. Resolved server-side, so the
      # page no longer downloads both 1536-number embeddings to make a second call with them.
      relevancyPercent
      # SCORED, PENDING, CV_UNREADABLE or NO_RESUME: why there is or is not a percentage.
      relevancyStatus
      jobSeeker {
        # The Candidate tab rendered email, phone and location from fields this query never
        # asked for, so every one of them read "-" while the record held them.
        personalDetails {
          fullName
          email
          phone
          city
          state
          country
        }
        jobPreferences {
          preferredCities
          noticePeriod
          preferredRoles
          jobType
        }
        overview,
        resumeUrl,
        internalAttributes { key value }
      }
      job {
        title,
        companyName
        companyId
        location
        # Managed Hiring ownership: who may move this candidate's status. Every internal user can
        # read the pipeline; only the assigned recruiter can advance a MANAGED one.
        hiringMode
        assignedHrId
        requirementStatus
        jobStatus
      }
      history {
        status,
        previousStatus,
        subStatus,
        closedReason,
        note,
        date,
        changedBy
      }
      notes {
        note,
        changedBy,
        date,
        visibility
      }
      feedback {
        rating,
        comment,
        stage,
        date,
        authorId
      }
      internalAttributes {
        key
        value
      }
      offeredSalary
      offeredSalaryCurrency
    }
  }
`;

/** Save the salary offered to a candidate for this role (Excel "salary offered"). */
export const jobApplicationOfferedSalarySave = (
  id: string,
  amount: number | null,
  currency = 'INR',
) => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateJobApplicationById(jobApplication: {
      _id: "${id}",
      offeredSalary: ${amount == null || Number.isNaN(amount) ? 'null' : Math.round(amount)},
      offeredSalaryCurrency: ${JSON.stringify(currency)},
      updatedBy: "${loginDetails.email ?? ''}"
    }) {
      _id
      offeredSalary
      offeredSalaryCurrency
    }
  }
`;
};

/**
 * Save the candidate's HR-only custom attributes (e.g. religion). Replaces the whole set.
 * These are stripped for employer/jobseeker callers by the backend, so the candidate never sees them.
 */
export const jobApplicationInternalAttributesSave = (
  id: string,
  attributes: { key: string; value: string }[],
) => {
  const loginDetails = currentUserDetails();
  const serialized = attributes
    .filter((a) => a.key && a.key.trim())
    .map(
      (a) =>
        `{ key: ${JSON.stringify(a.key.trim())}, value: ${JSON.stringify(a.value ?? '')} }`,
    )
    .join(', ');
  return `mutation {
    updateJobApplicationById(jobApplication: {
      _id: "${id}",
      internalAttributes: [${serialized}],
      updatedBy: "${loginDetails.email ?? ''}"
    }) {
      _id,
      internalAttributes { key value }
    }
  }
`;
};

export interface StatusChangeInput {
  status: string;
  subStatus?: string;
  closedReason?: string;
  closedNote?: string;
  /** Recorded on the history entry for this change. */
  statusNote?: string;
}

/**
 * Move a candidate. Stage, detail and (for Closed) reason travel together, because the backend
 * judges them together: a detail must belong to its stage, and Closed without a reason is refused.
 */
export const jobApplicationStatusUpdate = (id: string, change: StatusChangeInput) => {
  const loginDetails = currentUserDetails();
  const optional = (['subStatus', 'closedReason', 'closedNote', 'statusNote'] as const)
    .filter((k) => change[k])
    .map((k) => `\n      ${k}: ${gql(change[k])},`)
    .join('');
  return `mutation {
    updateJobApplicationById(jobApplication: {
      _id: ${gql(id)},
      status: ${gql(change.status)},${optional}
      updatedBy: ${gql(loginDetails.email ?? '')}
    }) {
      _id,
      status,
      subStatus,
      closedReason
    }
  }
`;
};

/**
 * Owner and next action. An empty next action clears it (the backend reads "" as clear), and an
 * owner is sent only when it changes, since only a Super Admin may change it.
 */
export const jobApplicationPlanSave = (
  id: string,
  plan: { ownerId?: string; nextAction?: string; nextActionDate?: string },
) => {
  const loginDetails = currentUserDetails();
  const fields = [
    plan.ownerId !== undefined ? `ownerId: ${gql(plan.ownerId)},` : '',
    plan.nextAction !== undefined ? `nextAction: ${gql(plan.nextAction)},` : '',
    plan.nextActionDate !== undefined ? `nextActionDate: ${gql(plan.nextActionDate)},` : '',
  ].filter(Boolean).join('\n      ');
  return `mutation {
    updateJobApplicationById(jobApplication: {
      _id: ${gql(id)},
      ${fields}
      updatedBy: ${gql(loginDetails.email ?? '')}
    }) {
      _id,
      ownerId,
      nextAction,
      nextActionAt
    }
  }
`;
};

/** Super Admin: reopen a Joined or Closed candidate, with the reason on record. */
export const reopenJobApplicationMutation = (id: string, status: string, reason: string) => `
  mutation {
    reopenJobApplication(_id: ${gql(id)}, status: ${gql(status)}, reason: ${gql(reason)}) {
      _id,
      status
    }
  }
`;

/** Records due today or overdue. A Super Admin may name an owner; an HR always gets their own. */
export const followUpsDueQuery = (ownerId?: string) => `
  query {
    getFollowUpsDue${ownerId ? `(ownerId: ${gql(ownerId)})` : ''} {
      content {
        _id,
        status,
        subStatus,
        nextAction,
        nextActionAt,
        ownerId,
        jobId,
        jobSeeker { _id, personalDetails { fullName } },
        job { title, companyName }
      }
      totalElements
    }
  }
`;

/**
 * A note is free text: line breaks and quotes are normal in it ("Experience - 10+ years" on one line,
 * "Current salary" on the next). It used to be pasted between quotes raw, so any multi-line note
 * was a GraphQL syntax error and was silently not saved (prod, 2026-10-16). Every value goes
 * through gql(), which escapes it.
 */
export const jobApplicationNoteSave = (id: string, note: string, visibility?: 'INTERNAL' | 'SHARED') => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateJobApplicationById(jobApplication: {
      _id: ${gql(id)},
      note: ${gql(note)},${visibility ? `\n      noteVisibility: ${gql(visibility)},` : ''}
      updatedBy: ${gql(loginDetails.email ?? '')}
    }) {
      _id,
      status,
      history {
        status,
        date,
        changedBy
      }
      notes {
        note,
        changedBy,
        date,
        visibility
      }
    }
  }
`;
};

/** Append a structured feedback entry (1-5 rating + optional comment) to an application. */
export const jobApplicationFeedbackSave = (
  id: string,
  rating: number | null,
  comment: string,
) => {
  const loginDetails = currentUserDetails();
  const safeComment = comment.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `mutation {
    updateJobApplicationById(jobApplication: {
      _id: "${id}",${rating != null ? `\n      rating: ${rating},` : ''}
      feedbackComment: "${safeComment}",
      updatedBy: "${loginDetails.email ?? ''}"
    }) {
      _id,
      feedback {
        rating,
        comment,
        date
      }
    }
  }
`;
};

// ------------------- Company Requests Queries -------------------
// Get all company requests
export const companyRequestsList = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllCompanyRequests(page: ${page}, size: ${size},sort: "createdAt", sortDirection: "desc", filters: [${filter}]) {
      content {
        _id,
        companyId,
        employeeId,
        createdAt,
        updatedAt,
        createdBy,
        updatedBy,
        status,
        requestNote
      }
      totalElements,
      totalPages
    }
  }
`;

// Get company request by ID
export const companyRequestById = (id: string) => `
  query {
    getCompanyRequest(_id: "${id}") {
      _id,
      companyId,
      employeeId,
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
      status,
      requestNote
    }
  }
`;

/**
 * Approve a company request and attach the employer, in one server-side call.
 *
 * Replaces the pair of `updateCompanyRequestStatus('approved')` + `updateEmployerCompanyId`.
 * Those ran back to back from the browser with no transaction between them, and the second is
 * the one that actually grants anything: if it failed, or the admin closed the tab, the request
 * read "approved" while the employer was attached to nothing, and the console offered no way to
 * finish the job because the request was no longer pending.
 */
export const approveCompanyRequest = (
  id: string,
  transferJobsFromEmployerId?: string,
  assignUnownedJobs?: boolean,
) => `
  mutation {
    approveCompanyRequest(_id: "${id}"${
      transferJobsFromEmployerId
        ? `, transferJobsFromEmployerId: "${transferJobsFromEmployerId}"`
        : ''
    }${assignUnownedJobs ? ', assignUnownedJobs: true' : ''}) {
      _id
      status
      companyId
      employeeId
    }
  }
`;

/**
 * Employers currently attached to a company.
 *
 * Used on the approval screen to answer the question an admin actually has: is somebody already
 * holding this company, and do their jobs need to come across? A claim is often a handover
 * rather than a first registration.
 */
export const employersByCompany = (companyId: string) => `
  query {
    getAllEmployers(page: 0, size: 50, filters: [{ key: "companyId", value: "${companyId}" }]) {
      content {
        _id
        personalDetails {
          fullName
          firstName
          lastName
          email
        }
      }
    }
  }
`;

/** Detach an employer from a company. Super-Admin only, enforced at the BFF. */
export const removeEmployerFromCompany = (companyId: string, employerId: string) => `
  mutation {
    removeEmployerFromCompany(companyId: "${companyId}", employerId: "${employerId}") {
      _id
      employers
    }
  }
`;

// Update updateCompanyRequestStatus
export const updateCompanyRequestStatus = (
  id: string,
  status: string
) => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
    updateCompanyRequest(companyRequest: {
      _id: "${id}",
      status: "${status}",
      updatedBy: "${loginDetails.email ?? ''}"
    }) {
      _id,
      status,
      requestNote
    }
  }
`;
};

// ------------------- Update Employer CompanyId -------------------
/*
 * `updateEmployerCompanyId` was removed with the atomic approval above.
 *
 * It attached an employer to a company on its own, which is the half of the old two-call
 * approval that granted access without recording that a decision had been made. Nothing calls it
 * any more, and leaving it exported is an invitation to reintroduce exactly the inconsistency
 * `approveCompanyRequest` exists to prevent.
 */


/**
 * Mark an employer as a premium (Managed) client, or remove that status. Super-Admin-only.
 * Removing premium also deactivates their dedicated-HR assignment server-side.
 */
export const setEmployerPremiumMutation = (
  clientId: string,
  isPremium: boolean,
  premiumTier?: string,
) => `
  mutation {
    setEmployerPremium(
      clientId: "${clientId}",
      isPremium: ${isPremium}${premiumTier ? `,\n      premiumTier: ${JSON.stringify(premiumTier)}` : ''}
    ) {
      _id
      isPremium
      premiumTier
      assignedHrId
    }
  }
`;

// ------------------- Premium request review + contracts -------------------

/** "Keep Regular" on a premium request: sets premiumRequestStatus=REJECTED, unblocking self-serve. */
export const rejectPremiumRequestMutation = (clientId: string) => `
  mutation {
    rejectPremiumRequest(clientId: "${clientId}") {
      _id
      isPremium
      premiumRequestStatus
    }
  }
`;

/* ---------------------- Countersigned agreements ---------------------- */

/**
 * Everything on file for one client, shared or not.
 *
 * Super-Admin only, enforced at the BFF. The employer's own view is a different query on a
 * different service that returns only what has been shared with them, deliberately not this one
 * with a flag.
 */
export const agreementsByEmployer = (employerId: string) => `
  query {
    getAgreementsByEmployer(employerId: "${employerId}") {
      _id
      title
      fileName
      fileSizeBytes
      s3Key
      signedAt
      expiresAt
      status
      visibleToEmployer
      uploadedAt
      uploadedBy
    }
  }
`;

/** The console's cross-client list, newest first. */
export const allAgreements = (page: number, size: number) => `
  query {
    getAllAgreements(page: ${page}, size: ${size}) {
      content {
        _id
        employerId
        title
        fileName
        fileSizeBytes
        s3Key
        signedAt
        expiresAt
        status
        visibleToEmployer
        uploadedAt
      }
      totalElements
      totalPages
    }
  }
`;

interface RecordAgreementArgs {
  employerId: string;
  s3Key: string;
  fileName?: string;
  fileSizeBytes?: number;
  title?: string;
  signedAt?: string;
  expiresAt?: string;
  visibleToEmployer?: boolean;
}

/** File an agreement already uploaded to the private agreements bucket. */
export const recordAgreementMutation = (a: RecordAgreementArgs) => `
  mutation {
    recordAgreement(agreement: {
      employerId: "${a.employerId}",
      s3Key: ${JSON.stringify(a.s3Key)}${a.fileName ? `,\n      fileName: ${JSON.stringify(a.fileName)}` : ''}${
        typeof a.fileSizeBytes === 'number' ? `,\n      fileSizeBytes: ${a.fileSizeBytes}` : ''
      }${a.title ? `,\n      title: ${JSON.stringify(a.title)}` : ''}${
        a.signedAt ? `,\n      signedAt: ${JSON.stringify(a.signedAt)}` : ''
      }${a.expiresAt ? `,\n      expiresAt: ${JSON.stringify(a.expiresAt)}` : ''},
      visibleToEmployer: ${!!a.visibleToEmployer}
    }) {
      _id
    }
  }
`;

/** Curate title, dates, status and employer visibility. Only the fields passed are changed. */
export const updateAgreementMutation = (a: {
  _id: string;
  title?: string;
  signedAt?: string;
  expiresAt?: string;
  status?: string;
  visibleToEmployer?: boolean;
}) => `
  mutation {
    updateAgreement(agreement: {
      _id: "${a._id}"${a.title !== undefined ? `,\n      title: ${JSON.stringify(a.title)}` : ''}${
        a.signedAt ? `,\n      signedAt: ${JSON.stringify(a.signedAt)}` : ''
      }${a.expiresAt ? `,\n      expiresAt: ${JSON.stringify(a.expiresAt)}` : ''}${
        a.status ? `,\n      status: ${JSON.stringify(a.status)}` : ''
      }${a.visibleToEmployer !== undefined ? `,\n      visibleToEmployer: ${a.visibleToEmployer}` : ''}
    }) {
      _id
      status
      visibleToEmployer
    }
  }
`;

/** Remove the filing. The bucket object is left in place. */
export const deleteAgreementMutation = (id: string) => `
  mutation {
    deleteAgreement(_id: "${id}")
  }
`;

/*
 * Read only, and deliberately the last two of these left.
 *
 * The console used to create, edit and delete contract templates on a page called Contracts. That
 * page is gone: it was mostly a template editor sitting next to a list of signed agreements, which
 * are a different object entirely. Templates themselves are still live, because SendContractDialog
 * fills one in and mails it, so the two reads stay and the writes went with the editor. Whatever
 * templates exist in the database remain sendable; new ones now need a backend change.
 */
export const getContractTemplatesQuery = `
  query {
    getContractTemplates {
      _id
      slug
      title
      status
      availableVariables
      updatedAt
      updatedBy
    }
  }
`;

export const getContractTemplateByIdQuery = (id: string) => `
  query {
    getContractTemplateById(id: "${id}") {
      _id
      slug
      title
      bodyHtml
      availableVariables
      status
      createdAt
      updatedAt
    }
  }
`;




/** Fill + send a contract to an employer. values are the admin-entered placeholder values. */
export const sendPremiumContractMutation = (
  employerId: string,
  templateSlug: string,
  values: { key: string; value: string }[],
) => {
  const valueArr = values
    .map(
      (v) => `{ key: ${JSON.stringify(v.key)}, value: ${JSON.stringify(v.value)} }`,
    )
    .join(', ');
  return `
  mutation {
    sendPremiumContract(
      employerId: "${employerId}",
      templateSlug: ${JSON.stringify(templateSlug)},
      values: [${valueArr}]
    ) {
      _id
      status
      sentAt
    }
  }
`;
};

// ------------------- Company Queries -------------------
export const companyById = (companyId: string) => `
  query {
    getCompanyById(_id: "${companyId}") {
      _id
      about
      status
      logoUrl
      companyDetails {
        email
        address
        city
        country
        state
        zipCode
        phoneNumber
      }
      links {
        instagram
        linkedin
        website
        behance
        googleDrive
        facebook
        twitter
      }
      companyName
      benefits
      skills
      employers
      jobsPosted
      tags
      isVerified
      isFeatured
      industry
      salesOwnerName
      discoveryAnswers { key value }
      metadata {
        title
        url
        description
        keywords
        metadata {
          key
          value
        }
      }
      createdAt,
      createdBy,
      updatedAt,
      updatedBy,
      notes {
        id,
        note,
        date,
        changedBy
      }
    }
  }
`;

/** Fetches only companyName for populating dropdown options (e.g. Jobs / JobApplicants filters). */
export const companyNamesList = (size = 5000) => `
  query {
    getAllCompanies(page: 0, size: ${size}, sort: "companyName", sortDirection: "asc", filters: [{ key: "status", value: "active" }]) {
      content {
        companyName
      }
    }
  }
`;

/** Server-side company-name search for the async filter (bounded — no full-list preload). */
export const companyNameSearch = (query: string, limit = 20) => `
  query {
    getAllCompanies(page: 0, size: ${limit}, sort: "companyName", sortDirection: "asc", filters: [{ key: "status", value: "active" }, { key: "companyName", value: ${JSON.stringify(query)} }]) {
      content {
        companyName
      }
    }
  }
`;

export const companyList = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllCompanies(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: [${filter}]) {
      totalPages,
      totalElements,
      content {
        _id,
        companyName,
        companyDetails {
          country,
          city,
          state,
          zipCode,
          address,
          locality
        },
        status,
        createdAt,
        history {
          date,
          status,
          changedBy
        }
      }
    }
  }
`;

/**
 * CompanyWorkspace renders a full "Skills & benefits" chip editor with a Save button, but
 * `skills`, `benefits` and `tags` were missing from this payload: every edit was accepted on
 * screen and silently discarded by the server, and reappeared on reload.
 *
 * They use JSON.stringify rather than the local `formatArray` because it escapes. A benefit
 * containing a double quote broke the entire query with formatArray.
 */
/*
 * industry was missing while the workspace rendered it as an editable field and showed it at a
 * glance. An admin could edit it, get "Saved", and watch it revert on reload -- the update simply
 * never carried it. It is not a rare field: 2,023 of 2,287 companies have one.
 *
 * This note sits outside the template deliberately. It was first written between `about` and
 * `industry`, inside the document, and GraphQL has no block comments -- only `#`. Every company
 * save from that point on shipped `Syntax Error: Unexpected character: "/"` and failed, which
 * reached production and was reported twice before anyone read the text being sent.
 * `test/graphQLDocumentsParse.test.ts` now parses what every builder here emits.
 */
export const companyUpdate = (companyId: string, companyData: any): string => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
  updateCompanyById(
    _id: ${gql(companyId)},
    company: {
      updatedBy: ${gql(loginDetails.email)},
      companyName: ${gql(companyData.companyName)},
      companyDetails: {city: ${gql(companyData.city)}, country: ${gql(companyData.country)}, email: ${gql(companyData.companyEmail)}, state: ${gql(companyData.state)}, zipCode: ${gql(companyData.zipCode)}, phoneNumber: ${gql(companyData.phoneNumber)}},
      about: ${gql(companyData.description)},
      industry: ${gql(companyData.industry)},
      links: {instagram: ${gql(companyData.instagramURL)}, linkedin: ${gql(companyData.linkedInURL)}, website: ${gql(companyData.website)}},
      logoUrl: ${gql(companyData.logoUrl)},
      status: ${gql(companyData.status)},
      salesOwnerName: ${gql(companyData.salesOwnerName)},
      skills: ${JSON.stringify(companyData.skills ?? [])},
      benefits: ${JSON.stringify(companyData.benefits ?? [])},
      tags: ${JSON.stringify(companyData.tags ?? [])},
      metadata: {
        title: ${gql(companyData.metadata?.title)}
        url: ${gql(companyData.metadata?.url)}
        description: ${gql(companyData.metadata?.description)}
        keywords: ${gql(companyData.metadata?.keywords)}
        metadata: ${graphQLArrayFormatter(companyData.metadata?.metadata ?? [])}
      }
    }
  ) {
    _id
  }
  }
`;
};

// Format array fields for GraphQL
const formatArray = (arr: string[]) => arr && arr.length ? `[${arr.map((v: string) => `"${v}"`).join(",")}]` : "[]";

export const createCompanyQuery = (data: CompanyFormData) => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
    createCompany(
      company: {
        companyName: "${data.companyName}",
        about:  ${JSON.stringify(data.description)},
        logoUrl: "${data.logoUrl || ''}",
        links: {
          website: "${data.website || ''}",
          instagram: "${data.instagramURL || ''}",
          linkedin: "${data.linkedInURL || ''}",
          facebook: "${data.facebook || ''}",
          behance: "${data.behance || ''}",
          googleDrive: "${data.googleDrive || ''}",
          twitter: "${data.twitter || ''}"
        },
        benefits: ${formatArray(data.benefits || [])},
        skills: ${formatArray(data.skills || [])},
        tags: ${formatArray(data.tags || [])},
        salesOwnerName: ${gql(data.salesOwnerName ?? '')},
        isVerified: ${data.isVerified || false},
        isFeatured: ${data.isFeatured || false},
        status: "${data.status || ''}",
        createdBy: "${loginDetails.email ?? ''}",
        metadata: {
          title: "${data.metadata?.title ?? ''}"
          url: "${data.metadata?.url ?? ''}"
          description: ${JSON.stringify(data.metadata?.description ?? '')}
          keywords: "${data.metadata?.keywords ?? ''}"
          metadata: ${graphQLArrayFormatter(data.metadata.metadata ?? [])}
        }
        industry: "${data.industry || ''}",
        companyDetails: {
          email: "${data.companyEmail || ''}",
          city: "${data.city || ''}",
          state: "${data.state || ''}",
          country: "${data.country || ''}",
          zipCode: "${data.zipCode || ''}",
          phoneNumber: "${data.phoneNumber || ''}",
          locality: "${(data as any).locality ?? ''}",
          placeId: "${(data as any).placeId ?? ''}",
          lat: ${(data as any).lat ?? 'null'},
          lng: ${(data as any).lng ?? 'null'}
        }
      }
    ) {
      _id
    }
  }
  `;
};

export const createUpdateStatusQuery = (id: string, status: string) => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
    updateCompanyById(
      _id: "${id}",
      company: { status: "${status}", updatedBy: "${loginDetails.email ?? ''}" }
    ) {
      _id
    }
  }
`;
};


// ------------------- Job Queries -------------------
export const jobList = (page: number, size: number, filter: string[] = []) => `
  query {
    getAllJobs(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: [${filter}]) {
      content {
        _id,
        postedDate,
        isExternalJob,
        companyName,
        title,
        location,
        locality,
        city,
        state,
        country,
        lat,
        lng,
        company {
          companyName
        },
        salary
        jobStatus
        requirementStatus
        # Soft delete. The row is kept, so the list can show it and restoreJob can undo it; without
        # these two the Deleted filter returns rows the column cannot label.
        deletedAt
        deletedBy
        hiringMode
        assignedHrId
        relevantCandidates {
        jobSeekerId
        jobSeekerName
        percentage
      }
        createdAt,
      }
      totalElements,
      totalPages
    }
  }
`;

// ------------------- User Queries -------------------
export const jobSeeker = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllJobSeekers(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters:[${filter}]) {
      content {
        _id,
        lastLoginAt,
        createdAt,
        personalDetails {
          fullName,
          locality,
          city,
          state,
          country,
          address,
          email,
          phone
        }
        jobPreferences {
        preferredLocalities
        preferredCities
        preferredStates
        preferredCountries
      }
      }
      totalElements,
      totalPages
    }
  }
`;

// ------------------- Employers Queries -------------------
export const employers = (
  page: number,
  size: number,
  filter: string[] = [],
) => `
  query {
    getAllEmployers(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters:[${filter}]) {
      content {
        _id,
        lastLoginAt,
        createdAt,
        isPremium,
        premiumRequestStatus,
        personalDetails {
          fullName,
          firstName,
          lastName,
          city,
          state,
          country,
          email,
          phone
        }
      }
      totalElements,
      totalPages
    }
  }
`;

/** All admin/HR users (id → name) — used to resolve note & history authorId to a readable name. */
export const getAllUsersForNames = `
  query {
    getAllUsers(page: 0, size: 500, sort: "createdAt", sortDirection: "desc", filters: []) {
      content {
        _id
        personalDetails {
          firstName
          lastName
          fullName
        }
      }
    }
  }
`;

// ── Premium Managed Hiring (C1) ─────────────────────────────────────────────

/**
 * Premium (Managed-plan) employers only, with the assigned-HR denormalized field.
 *
 * `companyId` rides along because the managed engagement is scoped to an *employer* -- isPremium,
 * premiumTier and assignedHrId are all fields on Employer -- while the jobs that engagement is
 * about hang off the employer's *company*. The Assign-jobs dialog has to cross that gap, and
 * without the id here it could only ask for jobs the account itself owns, which is almost none
 * of them.
 */
export const premiumEmployers = (page: number, size: number) => `
  query {
    getAllEmployers(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters:[{ key: "isPremium", value: "true" }]) {
      content {
        _id,
        isPremium,
        premiumTier,
        assignedHrId,
        createdAt,
        companyId,
        company { companyName },
        personalDetails {
          fullName,
          firstName,
          lastName,
          email,
          phone,
          city,
          state,
          country
        }
      }
      totalElements,
      totalPages
    }
  }
`;

/** Assign or reassign a dedicated HR to a premium client (Super-Admin action). */
export const assignHrMutation = (
  clientId: string,
  hrUserId: string,
  role: 'PRIMARY' | 'SECONDARY' = 'PRIMARY',
) => `
  mutation {
    assignHr(clientId: "${clientId}", hrUserId: "${hrUserId}", role: "${role}") {
      _id,
      clientId,
      hrUserId,
      role,
      active,
      assignedAt
    }
  }
`;

/** All active HR owners (primary + secondary) for a client. */
export const getActiveHrAssignmentsQuery = (clientId: string) => `
  query {
    getActiveHrAssignments(clientId: "${clientId}") {
      _id
      clientId
      hrUserId
      role
      active
      assignedAt
    }
  }
`;

/** The clients currently assigned to an HR — powers the "My Accounts" workspace (C2). */
export const hrWorklist = (hrUserId: string) => `
  query {
    getHrWorklist(hrUserId: "${hrUserId}") {
      _id,
      clientId,
      hrUserId,
      active,
      assignedAt
    }
  }
`;

/**
 * The signed-in HR's job worklist: MANAGED jobs assigned to them.
 *
 * The assignedHrId filter is this query's own doing, not the server's. This used to claim
 * the backend scoped getAllJobs to the caller's assignedHrId, and pass hiringMode alone on
 * the strength of it. JobsService.getAllJobs (JobsService.java:106-136) passes an empty
 * scope list on purpose -- hiding a colleague's MANAGED job made two recruiters working one
 * client likelier, not less likely, so visibility was split from access deliberately. The
 * list is unscoped for everyone, and "My Accounts" was rendering every MANAGED job on the
 * platform as a card reading 0 candidates.
 *
 * Caller must pass a non-empty hrUserId. An empty value is dropped by the criteria builder
 * rather than matching nothing, which silently restores the all-platform behaviour.
 */
export const hrManagedJobs = (page: number, size: number, hrUserId: string) => `
  query {
    getAllJobs(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: [{ key: "hiringMode", value: "MANAGED" }, { key: "assignedHrId", value: "${hrUserId}" }]) {
      content {
        _id,
        title,
        companyName,
        employerId,
        jobType,
        jobStatus,
        assignedHrId,
        company { companyName }
      }
      totalElements
    }
  }
`;

/**
 * Lean applications feed for the HR worklist: just what's needed to tally candidates
 * per job (jobId) and flag those awaiting feedback. Scoped to the caller's assigned jobs.
 */
export const hrWorklistApplications = (page: number, size: number) => `
  query {
    getAllJobApplications(page: ${page}, size: ${size}, sort: "createdAt", sortDirection: "desc", filters: []) {
      content {
        _id,
        status,
        subStatus,
        jobId
      }
      totalElements
    }
  }
`;

/** Interviews for a client's employer — aggregated into the HR upcoming-interviews list. */
export const interviewsForEmployer = (employerId: string) => `
  query {
    getInterviewsForEmployer(employerId: "${employerId}") {
      _id,
      applicationId,
      jobSeekerId,
      round,
      scheduledAt,
      meetingProvider,
      meetingLink,
      autoGenerated,
      outcome
    }
  }
`;

/** Record an interview's attendance outcome (ATTENDED | NO_SHOW | RESCHEDULED | CANCELLED). */
export const markInterviewOutcomeMutation = (interviewId: string, outcome: string) => `
  mutation {
    markInterviewOutcome(interviewId: "${interviewId}", outcome: "${outcome}") {
      _id
      outcome
    }
  }
`;

export const getJobApplicationsByJobSeekerId = (jobSeekerId: string) => `
  query {
    getAllJobApplications(page: 0, size: 100, sort: "createdAt", sortDirection: "desc", filters: [{ key: "jobSeekerId", value: "${jobSeekerId}" }]) {
      content {
        _id,
        status,
        subStatus,
        closedReason,
        ownerId,
        nextAction,
        nextActionAt,
        createdAt,
        jobId,
        job {
          title,
          companyName,
          location,
        }
      }
      totalElements
    }
  }
`;

/** Save the candidate's HR-only custom attributes (e.g. religion). Replaces the whole set. */
/**
 * Save a candidate's languages.
 *
 * Its own mutation rather than a field on the internal-attributes save, for the same reason
 * `screeningAnswers` is a separate field on the model: `jobSeekerInternalAttributesSave` replaces
 * the WHOLE attributes list, so anything sharing that call can be dropped by an unrelated edit to
 * a free-text row. Languages are used to answer a client brief; losing them silently because
 * somebody tidied a "Religion" row is not a trade worth making.
 *
 * An empty array is sent as an empty array, deliberately: that is how a recruiter clears the
 * field. The backend merges with `Optional.ofNullable(...).ifPresent(...)`, so `[]` clears and
 * absent preserves.
 */
export const jobSeekerLanguagesSave = (id: string, languages: string[]) => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateJobSeekerById(_id: ${gql(id)}, jobSeeker: {
      updatedBy: ${gql(loginDetails.email ?? '')},
      languages: ${JSON.stringify(languages ?? [])}
    }) {
      _id
      languages
    }
  }
`;
};

export const jobSeekerInternalAttributesSave = (
  id: string,
  attributes: { key: string; value: string }[],
) => {
  const loginDetails = currentUserDetails();
  const serialized = attributes
    .filter((a) => a.key && a.key.trim())
    .map(
      (a) =>
        `{ key: ${JSON.stringify(a.key.trim())}, value: ${JSON.stringify(a.value ?? '')} }`,
    )
    .join(', ');
  return `mutation {
    updateJobSeekerById(_id: "${id}", jobSeeker: {
      updatedBy: "${loginDetails.email ?? ''}",
      internalAttributes: [${serialized}]
    }) {
      _id
      internalAttributes { key value }
    }
  }
`;
};

/**
 * Save the five fixed screening answers on a candidate, or the five discovery answers on a
 * company. Both replace the whole set server-side, so the caller sends every question's key --
 * a blank answer is stored as an empty string, not omitted, which is what keeps "asked and
 * declined to say" distinct from "never asked".
 */
const serializeAnswers = (answers: { key: string; value: string }[]) =>
  answers
    .filter((a) => a.key && a.key.trim())
    .map(
      (a) => `{ key: ${JSON.stringify(a.key.trim())}, value: ${JSON.stringify(a.value ?? '')} }`,
    )
    .join(', ');

export const jobSeekerScreeningAnswersSave = (
  id: string,
  answers: { key: string; value: string }[],
) => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateJobSeekerById(_id: "${id}", jobSeeker: {
      updatedBy: "${loginDetails.email ?? ''}",
      screeningAnswers: [${serializeAnswers(answers)}]
    }) {
      _id
      screeningAnswers { key value }
    }
  }
`;
};

export const companyDiscoveryAnswersSave = (
  id: string,
  answers: { key: string; value: string }[],
) => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateCompanyById(_id: "${id}", company: {
      updatedBy: "${loginDetails.email ?? ''}",
      discoveryAnswers: [${serializeAnswers(answers)}]
    }) {
      _id
      discoveryAnswers { key value }
    }
  }
`;
};

export const getJobSeekerById = (id: string) => `
  query {
    getJobSeekerById(_id: "${id}") {
      _id,
      isExternal,
      lastLoginAt,
      createdAt,
      createdBy,
      updatedAt,
      updatedBy,
      currentCompany,
      currentLocation,
      currentRole,
      currentSalary,
      currentSalaryAmount,
      currentSalaryCurrency,
      currentSalaryFrequency,
      workHistory { company title location from to isCurrent },
      educationEntries { degree fieldOfStudy institute startYear endYear },
      certifications { name issuer year },
      links { linkedin instagram website behance googleDrive facebook twitter },
      portfolio { imageUrl caption uploadedAt },
      experience,
      subIndustry,
      industry,
      jobSeekerDescription,
      jobPreferences {
        jobType,
        preferredCities,
        preferredStates,
        preferredCountries,
        preferredLocalities,
        noticePeriod,
        noticePeriodDays,
        preferredRoles,
        salaryExpectation,
        salaryExpectationMin,
        salaryExpectationMax,
        salaryExpectationCurrency,
        salaryExpectationFrequency,
        jobCategory
      },
      personalDetails {
        address,
        gender,
        city,
        country,
        email,
        fullName,
        firstName,
        lastName,
        phone,
        state,
        zipCode,
        birthday,
        locality,
        placeId,
        lat,
        lng
      },
      yearsOfExperience,
      resumeUrl,
      skills,
      overview,
      education,
      embedding {
        vectorV2
        updatedAt
      }
      ,
      internalAttributes { key value },
      languages,
      screeningAnswers { key value },
      notes {
        id,
        note,
        date,
        changedBy
      }
    }
  }
`;

/** Save the managed-hiring onboarding checklist (key = step, value = "done" or empty). */
export const employerOnboardingSave = (
  id: string,
  steps: { key: string; value: string }[],
) => {
  const loginDetails = currentUserDetails();
  const serialized = steps
    .map((s) => `{ key: ${JSON.stringify(s.key)}, value: ${JSON.stringify(s.value ?? '')} }`)
    .join(', ');
  return `mutation {
    updateEmployerById(_id: "${id}", employer: {
      updatedBy: "${loginDetails.email ?? ''}",
      onboardingSteps: [${serialized}]
    }) {
      _id
      onboardingSteps { key value }
    }
  }
`;
};

export const getEmployerById = (id: string) => `
  query {
    getEmployerById(_id: "${id}") {
      _id,
      isExternal,
      authProvider,
      isPremium,
      premiumTier,
      assignedHrId,
      premiumRequestStatus,
      premiumRequestedAt,
      approvalStatus,
      approvalReason,
      approvalDecidedAt,
      approvalDecidedBy,
      # Self-declared at registration, unverified. Read here so the approval decision is made
      # with the declaration on screen rather than inferred from the company name.
      businessModel,
      hiringFor,
      onboardingSteps { key value },
      lastLoginAt,
      createdAt,
      createdBy,
      updatedAt,
      updatedBy,
      companyId,
      planType,
      jobValidityDays,
      # Set when a Super Admin cancels the plan (or records a refund, which cancels it too).
      planCancelledAt,
      planCancelledBy,
      planCancelReason,
      company {
        companyName
        logoUrl
      }
      personalDetails {
        address,
        city,
        country,
        email,
        fullName,
        firstName,
        lastName,
        phone,
        state,
        zipCode,
        birthday
      }
      jobsPosted
      jobsLeft
      links {
        instagram
        linkedin
        website
        behance
        googleDrive
        facebook
        twitter
      }
    }
  }
`;

/*
 * No jobsLeft, jobsPosted, planType or jobValidityDays here. The workspace only displays them, but
 * this mutation sent back whatever it had loaded (`jobsLeft || 0`), so saving a profile restored
 * credits the employer had since spent, or zeroed them. Credits are written only by a verified
 * payment, posting a job, or a promo redemption, and the backend now ignores them on this mutation.
 */
export const updateEmployer = (data: EmployersFormData) => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
    updateEmployerById(
      _id: ${gql(data.id)},
      employer: {
        companyId: ${gql(data.companyId)},
        updatedBy: ${gql(loginDetails.email)},
        personalDetails: {
          city: ${gql(data.city)},
          country: ${gql(data.country)},
          email: ${gql(data.email)},
          firstName: ${gql(data.firstName)},
          lastName: ${gql(data.lastName)},
          state: ${gql(data.state)},
          phone: ${gql(data.phone)},
          zipCode: ${gql(data.zipcode)},
          locality: ${gql((data as any).locality)},
          placeId: ${gql((data as any).placeId)},
          lat: ${(data as any).lat ?? 'null'},
          lng: ${(data as any).lng ?? 'null'}
        },
        links: {
          instagram: ${gql(data.links?.instagram)},
          linkedin: ${gql(data.links?.linkedin)},
          website: ${gql(data.links?.website)},
          behance: ${gql(data.links?.behance)},
          googleDrive: ${gql(data.links?.googleDrive)},
          facebook: ${gql(data.links?.facebook)},
          twitter: ${gql(data.links?.twitter)}
        }
      }
    ) {
      _id
    }
  }
`;
};

export const updateUser = (data: UserFormData) => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
    updateUserById(
      _id: "${data.id}", 
      user: {
        updatedBy: ${JSON.stringify(loginDetails.email ?? '')},
        personalDetails: {
          ${
            /*
             * Only the fields the caller actually supplied.
             *
             * personalDetails is a nested object, so sending it replaces the whole sub-document.
             * Every key here defaulted to "", and the profile form passes just first name, last
             * name and email -- so saving your name also blanked address, city, country, state,
             * phone and zipCode. They happen to be empty on the current user records, which is the
             * only reason nobody had lost anything yet.
             */
            [
              ['address', data.address],
              ['city', data.city],
              ['country', data.country],
              ['email', data.email],
              ['firstName', data.firstName],
              ['lastName', data.lastName],
              ['state', data.state],
              ['phone', data.phone],
              ['zipCode', data.zipcode],
            ]
              .filter(([, v]) => v !== undefined && v !== null)
              .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
              .join('\n          ')
          }
        }
      }
    ) {
      _id
    }
  }
`;
}

/**
 * A GraphQL input literal for plain data. Every string goes through gql(), so a value containing a
 * quote, a backslash or a line break is escaped rather than ending the literal. Keys are our own
 * field names, never user input.
 */
const gqlLiteral = (v: unknown): string => {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return gql(v);
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : 'null';
  if (typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.map(gqlLiteral).join(', ')}]`;
  if (typeof v === 'object') {
    return `{ ${Object.entries(v as Record<string, unknown>)
      .filter(([, x]) => x !== undefined)
      .map(([k, x]) => `${k}: ${gqlLiteral(x)}`)
      .join(', ')} }`;
  }
  return 'null';
};

/** Drop rows where every field is blank: an "Add" that was never filled in is not an entry. */
const nonEmptyEntries = <T extends object>(rows: T[] | undefined | null): T[] =>
  (rows ?? []).filter((r) =>
    Object.values(r).some((x) => (typeof x === 'string' ? x.trim() !== '' : x != null && x !== false)),
  );

/**
 * Save the whole job-seeker form.
 *
 * Rebuilt on gql()/gqlLiteral. It interpolated raw strings -- `currentRole: "${data.currentRole}"`
 * -- so a company called `Shah "Jewels"`, or a note with a line break, ended the string literal
 * and the whole save failed to parse. updateJobDataById made the same move for jobs.
 *
 * `overview` is sent now that the workspace shows and edits it; it was fetched and never saved.
 */
export const updateJobSeeker = (data: JobSeekerFormData) => {
  const loginDetails = currentUserDetails();
  const locationArr = Array.isArray(data.location) ? data.location : (data.location ? [data.location] : []);
  const preferredRolesArr = Array.isArray(data.preferredRoles) ? data.preferredRoles : (data.preferredRoles ? [data.preferredRoles] : []);
  const input = {
    updatedBy: loginDetails.email ?? '',
    jobPreferences: {
      jobType: data.jobType || '',
      preferredCities: locationArr,
      preferredStates: data.preferredStates || [],
      preferredCountries: data.preferredCountries || [],
      preferredLocalities: data.preferredLocalities || [],
      noticePeriod: data.noticePeriod || '',
      noticePeriodDays: data.noticePeriodDays ?? null,
      preferredRoles: preferredRolesArr,
      salaryExpectation: data.salaryExpectation || '',
      salaryExpectationMin: data.salaryExpectationMin ?? null,
      salaryExpectationMax: data.salaryExpectationMax ?? null,
      salaryExpectationCurrency: data.salaryExpectationCurrency || 'INR',
      salaryExpectationFrequency:
        normalizeSalaryFrequency(data.salaryExpectationFrequency) ?? (data.salaryExpectationFrequency || ''),
      jobCategory: data.jobCategory || '',
    },
    personalDetails: {
      city: data.city || '',
      country: data.country || '',
      email: data.email || '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      gender: data.gender || '',
      address: data.address || '',
      state: data.state || '',
      phone: data.phone || '',
      zipCode: data.zipcode || '',
      locality: data.locality ?? '',
      placeId: data.placeId ?? '',
      lat: data.lat ?? null,
      lng: data.lng ?? null,
    },
    currentCompany: data.currentCompany || '',
    currentLocation: data.currentLocation || '',
    currentRole: data.currentRole || '',
    currentSalary: data.currentSalary || '',
    currentSalaryAmount: data.currentSalaryAmount ?? null,
    currentSalaryCurrency: data.currentSalaryCurrency || '',
    currentSalaryFrequency:
      normalizeSalaryFrequency(data.currentSalaryFrequency) ?? (data.currentSalaryFrequency || ''),
    workHistory: nonEmptyEntries(data.workHistory),
    educationEntries: nonEmptyEntries(data.educationEntries),
    certifications: nonEmptyEntries(data.certifications),
    links: data.links,
    experience: data.experience || '',
    subIndustry: data.subIndustry || '',
    industry: data.industry || '',
    jobSeekerDescription: data.jobSeekerDescription || '',
    overview: data.overview || '',
    education: data.education || '',
    skills: (data.skills || []).filter(Boolean),
    resumeUrl: data.resumeUrl || '',
    yearsOfExperience: Math.trunc(Number(data.yearsOfExperience) || 0),
  };
  return `
  mutation {
    updateJobSeekerById(_id: ${gql(data.id)}, jobSeeker: ${gqlLiteral(input)}) {
      _id
    }
  }
`;
};

export const getJobDataById = (id: string) => {
  return `query MyQuery {
    getJobById(_id: "${id}") {
      _id
      jobCode
      jobUrl
      jobSource
      hiringMode
      assignedHrId
      title
      description
      companyId
      companyName
      company {
        companyName
        logoUrl
      }
      jobStatus
      requirementStatus
      requirementReason
      activeAt
      filledAt
      requirementHistory { status previousStatus note date changedBy }
      jobType
      industry
      category
      description
      expiryDate
      location
      locality
      city
      state
      country
      zipCode
      placeId
      lat
      lng
      salesOwnerName
      salesOwnerId
      numberOfPositions
      internalFilters {
        key
        value
      }
      postedDate
      salary
      salaryCurrency
      salaryAmount
      salaryMin
      salaryMax
      salaryFrequency
      ageLimit
      gender
      experience
      yearsOfExperience
      education
      responsibilities
      skillsRequired
      mustHaveSkills
      niceToHaveSkills
      isRemote
      requirements
      tags
      isExternalJob
      isVerified
      isFeatured
      emailAddress
      phoneNumber
      summary
      postType
      seoPosting
      sponsored
      employerId
      metadata {
        title
        url
        description
        keywords
        metadata {
          key
          value
        }
      }
      embeddings
      embeddingsV2
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
  }`;
};

/**
 * ---------------------------------------------------------------------------------------------
 * Job mutation payloads: one emitter table, shared by create and update.
 * ---------------------------------------------------------------------------------------------
 *
 * **Why a table instead of a template literal.**
 *
 * `updateJobDataById` used to spell out every field inline, and it took `JobFormData` -- a
 * 60-field interface. `JobWorkspace` passes a NARROWER object (its local `Form`, which has no
 * `employerId`, `ageLimit`, `gender`, `postType`, `sponsored`, `salaryAmount`, `locality`,
 * `placeId`, `mustHaveSkills` or `niceToHaveSkills`) through an `as any` cast, so those ten keys
 * were `undefined` and interpolated as:
 *
 *     employerId: "undefined"        <- the literal string, written to Mongo
 *     ageLimit: ""                   <- `|| ''`, so an empty string
 *     mustHaveSkills: []             <- `?? []`, wiping the agentic skills list
 *
 * The backend merges per field with `Optional.ofNullable(x).ifPresent(setter)`
 * (`JobUpdateHelper.updateExistingJob`): `null` is skipped and the stored value survives, but
 * `""` is *present*, so it is written. Saving any one card therefore blanked six fields on the
 * record and detached its employer -- silently, because the mutation itself succeeded.
 *
 * The fix is to emit only the keys the caller actually supplied. That is not a workaround; it is
 * the contract the server declares. An absent key is designed to preserve.
 *
 * This is the same fix, for the same bug, that `updateUser` above already carries -- its comment
 * describes saving a name blanking the address. This table generalises it so the decision is made
 * once per field rather than once per mutation.
 *
 * The second thing the table buys is that **escaping and enum handling stop being per-field
 * choices**. Every string goes through `gql` (JSON.stringify), so there is no way to add a field
 * that a quote or a newline in the data can break -- previously ~31 fields were interpolated raw
 * between hand-written quotes. And every enum goes through a normaliser that OMITS the key when
 * the value is not a legal member, so a bad value can no longer produce a parse error.
 */

/** The right-hand side of `field: <value>`, already escaped. `undefined` means "omit the key". */
type JobFieldEmitter = (value: unknown) => string | undefined;

const jobEmit = {
  str: (v: unknown) => gql(v),
  /** For an id that is legitimately nullable: emits the `null` literal rather than `""`. */
  strOrNull: (v: unknown) => (v == null ? 'null' : gql(v)),
  num: (v: unknown) => (v == null || v === '' ? 'null' : String(Number(v))),
  int: (v: unknown) => (v == null || v === '' ? 'null' : String(Math.trunc(Number(v)))),
  bool: (v: unknown) => String(Boolean(v)),
  boolOrNull: (v: unknown) => (v == null ? 'null' : String(Boolean(v))),
  strArr: (v: unknown) => JSON.stringify(Array.isArray(v) ? v : []),
  kvArr: (v: unknown) => graphQLArrayFormatter(sanitizeKeyValues(v as { key: string; value: string }[])),
  /*
   * Enum literals. Unquoted by GraphQL's syntax, which is exactly why the old code broke: it
   * interpolated the DISPLAY string, so `jobType: Full Time` was a parse error and
   * `jobStatus: Open` an invalid member of {OPEN, CLOSED, EXPIRED}. Returning undefined for an
   * unrecognised value omits the key, and the backend then preserves what is stored -- strictly
   * better than sending a guess or a document that will not parse.
   */
  jobType: (v: unknown) => toJobTypeEnum(v),
  jobStatus: (v: unknown) => toJobStatusEnum(v),
  /*
   * Pay frequency is a plain String on the backend with no enum and no validation, and the
   * workspace used to offer a free-text box, so the stored values include 'monthly', 'Per month'
   * and ''. Canonicalise what we recognise on the way out; pass anything else through untouched
   * rather than inventing a value or silently clearing the field.
   */
  freq: (v: unknown) => gql(normalizeSalaryFrequency(v) ?? String(v ?? '')),
  metadata: (v: unknown) => {
    const m = (v ?? {}) as JobFormData['metadata'];
    return `{
          title: ${gql(m?.title)}
          url: ${gql(m?.url)}
          description: ${gql(m?.description)}
          keywords: ${gql(m?.keywords)}
          metadata: ${graphQLArrayFormatter(m?.metadata ?? [])}
        }`;
  },
} satisfies Record<string, JobFieldEmitter>;

/**
 * Every field both job mutations accept, in the order they were previously written.
 *
 * `location` is absent on purpose: the update sends `data.location` verbatim while the create
 * derives it from city/state/country, so each caller supplies its own.
 */
const JOB_FIELDS: ReadonlyArray<readonly [keyof JobFormData, JobFieldEmitter]> = [
  ['title', jobEmit.str],
  ['description', jobEmit.str],
  ['jobType', jobEmit.jobType],
  ['jobStatus', jobEmit.jobStatus],
  ['industry', jobEmit.str],
  ['category', jobEmit.str],
  ['city', jobEmit.str],
  ['state', jobEmit.str],
  ['country', jobEmit.str],
  ['zipCode', jobEmit.str],
  ['salary', jobEmit.str],
  ['salaryCurrency', jobEmit.str],
  ['salaryAmount', jobEmit.str],
  ['salaryMin', jobEmit.num],
  ['salaryMax', jobEmit.num],
  ['salaryFrequency', jobEmit.freq],
  ['ageLimit', jobEmit.str],
  ['gender', jobEmit.str],
  ['expiryDate', jobEmit.str],
  ['emailAddress', jobEmit.str],
  ['phoneNumber', jobEmit.str],
  ['summary', jobEmit.str],
  ['isFeatured', jobEmit.bool],
  ['isVerified', jobEmit.bool],
  ['postType', jobEmit.str],
  ['seoPosting', jobEmit.bool],
  ['sponsored', jobEmit.str],
  ['jobCode', jobEmit.str],
  ['experience', jobEmit.str],
  ['yearsOfExperience', jobEmit.int],
  ['education', jobEmit.str],
  ['skillsRequired', jobEmit.strArr],
  ['mustHaveSkills', jobEmit.strArr],
  ['niceToHaveSkills', jobEmit.strArr],
  ['isRemote', jobEmit.bool],
  ['responsibilities', jobEmit.str],
  ['tags', jobEmit.strArr],
  ['metadata', jobEmit.metadata],
  ['isExternalJob', jobEmit.bool],
  ['jobUrl', jobEmit.str],
  ['jobSource', jobEmit.str],
  ['companyName', jobEmit.str],
  ['requirements', jobEmit.str],
  ['companyId', jobEmit.strOrNull],
  ['employerId', jobEmit.strOrNull],
  ['locality', jobEmit.str],
  ['placeId', jobEmit.str],
  ['lat', jobEmit.num],
  ['lng', jobEmit.num],
  ['salesOwnerName', jobEmit.str],
  ['salesOwnerId', jobEmit.str],
  ['numberOfPositions', jobEmit.num],
  // New / Active / On Hold / Filled / Closed. The backend derives jobStatus from it.
  ['requirementStatus', jobEmit.str],
  ['requirementReason', jobEmit.str],
  ['internalFilters', jobEmit.kvArr],
  ['dataAvailable', jobEmit.boolOrNull],
];

/**
 * `field: value` lines for every key the caller actually supplied.
 *
 * A key whose value is `undefined` is skipped entirely, which is the whole point: the backend
 * preserves what it is not told about. A key whose emitter returns `undefined` (an unrecognised
 * enum) is skipped for the same reason.
 */
const buildJobFields = (data: Partial<JobFormData>): string =>
  JOB_FIELDS.reduce<string[]>((lines, [key, emitValue]) => {
    if (data[key] === undefined) return lines;
    const value = emitValue(data[key]);
    if (value === undefined) return lines;
    lines.push(`${key}: ${value}`);
    return lines;
  }, []).join('\n        ');

/**
 * Update a job.
 *
 * Takes a `Partial`, deliberately: `JobWorkspace` edits one card at a time and sends only that
 * card's fields, so everything else on the record is left alone by construction.
 */
export const updateJobDataById = (data: Partial<JobFormData> & { id: string }) => {
  const loginDetails = currentUserDetails();
  const fields = buildJobFields(data);
  return `mutation MyMutation {
    updateJobById(
      _id: ${gql(data.id)}
      job: {
        updatedBy: ${gql(loginDetails.email ?? '')}
        ${data.location !== undefined ? `location: ${gql(data.location)}` : ''}
        ${fields}
      }
    ) {
      _id
    }
  }`;
};

// createJobData

export const createJobData = (data: JobFormData) => {
  const loginDetails = currentUserDetails();
  const fields = buildJobFields(data);
  return `mutation MyMutation {
    createJob(
      job: {
        userType: "ADMIN"
        createdBy: ${gql(loginDetails.email ?? '')}
        location: ${gql(`${data.city}, ${data.state}, ${data.country}`)}
        companyLogo: ${gql((data as unknown as { companyLogo?: string }).companyLogo ?? '')}
        ${
          /*
           * Emitted only when a Super Admin chose one. createJob derives hiringMode from the
           * employer's isPremium flag when it is absent, and sending SELF_SERVE from a control
           * nobody touched would silently override that -- turning every premium client's new
           * roles self-serve, which is the opposite of the intent.
           *
           * assignedHrId rides along only with MANAGED: an id on a self-serve job is a recruiter
           * nobody can act on, and the access rule keys off hiringMode, so it would sit there
           * describing nothing.
           */
          data.hiringMode ? `hiringMode: ${gql(data.hiringMode)}` : ''
        }
        ${
          data.hiringMode === 'MANAGED' && data.assignedHrId
            ? `assignedHrId: ${gql(data.assignedHrId)}`
            : ''
        }
        ${fields}
      }
      ) {
      _id
    }
  }`;
};

// Relevancy Queries

export const getRelevancyScore = (jobId: string, jobSeekerId: string) => {
  return `query {
    getRelevancyScore(jobId: "${jobId}", jobSeekerId: "${jobSeekerId}")
  }`;
}

export const createJobSeekerOverview = (jobSeekerId: string) => {
  return `mutation createJobSeekerOverview {
    createJobSeekerOverview(jobSeekerId: "${jobSeekerId}") {
      _id
    }
  }`;
}

// ------------------- Resume Uploads (Import) -------------------
export const createResumeUploadMutation = (input: { fileName: string; fileType?: string; fileSize?: number; fileUrl?: string; bucketKey?: string; contentHash?: string; createdBy?: string; }) => {
  const loginDetails = currentUserDetails();
  const createdBy = loginDetails?.email || input.createdBy || '';
  return `mutation {
    createResumeUpload(record: {
      fileName: "${input.fileName}",
      fileType: "${input.fileType || ''}",
      fileSize: ${input.fileSize || 0},
      fileUrl: "${input.fileUrl || ''}",
      bucketKey: "${input.bucketKey || ''}",
      contentHash: "${input.contentHash || ''}",
      createdBy: "${createdBy}"
    }) {
      _id
      fileName
      fileUrl
      bucketKey
      status
      createdAt
    }
  }`;
};

export const listResumeUploadsByStatusQuery = (status: UploadStatus | string) => `query {
  listResumeUploadsByStatus(status: ${status}) {
    _id
    fileName
    fileType
    fileSize
    fileUrl
    bucketKey
    status
    errorMessage
    jobSeekerId
    jobSeekerName
    createdAt
    updatedAt
  }
}`;

// Paginated version: returns a ResumeUploadPage { content, totalPages, totalElements }
export const listResumeUploadsByStatusPageQuery = (
  status: UploadStatus | string,
  page: number,
  size: number,
  sort: string = 'createdAt',
  sortDirection: string = 'desc',
) => `query {
  listResumeUploadsByStatus(status: ${status}, page: ${page}, size: ${size}, sort: "${sort}", sortDirection: "${sortDirection}") {
    content {
      _id
      fileName
      fileType
      fileSize
      fileUrl
      bucketKey
      status
      errorMessage
      jobSeekerId
      jobSeekerName
      createdAt
      updatedAt
    }
    totalPages
    totalElements
  }
}`;

export const listResumeUploadsQuery = (
  status: string | undefined,
  fromDate: string | undefined,
  toDate: string | undefined,
  page: number,
  size: number,
  sort: string = 'createdAt',
  sortDirection: string = 'desc',
) => {
  const args: string[] = [];
  if (status) args.push(`status: "${status}"`);
  if (fromDate) args.push(`fromDate: "${fromDate}"`);
  if (toDate) args.push(`toDate: "${toDate}"`);
  args.push(`page: ${page}`, `size: ${size}`, `sort: "${sort}"`, `sortDirection: "${sortDirection}"`);
  return `query {
  listResumeUploads(${args.join(', ')}) {
    content {
      _id
      fileName
      fileType
      fileSize
      fileUrl
      bucketKey
      status
      errorMessage
      jobSeekerId
      jobSeekerName
      createdAt
      updatedAt
    }
    totalPages
    totalElements
  }
}`;
};

// GraphQL enum for upload status (matches backend UploadStatus enum)
export enum UploadStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  /** Parsed fine, but the CV carried no email — the one key that can ever match this person. */
  NEEDS_EMAIL = 'NEEDS_EMAIL',
  /** Matched an existing unclaimed profile, which was overwritten rather than duplicated. */
  REPLACED = 'REPLACED',
  /** Matched an account somebody signed up for. Their own data wins; nothing was written. */
  REJECTED_EXISTING_USER = 'REJECTED_EXISTING_USER',
}

/**
 * Every status must appear here, because the import page derives both its filter chips and its
 * "All" total from Object.values(UploadStatus). A value the enum does not know about is a row that
 * shows up in no tab and is missing from the count, which reads as "the upload vanished".
 */

export const listResumeUploadsByFileNameQuery = (fileName: string) => `query {
  getResumeUploadByFileName(fileName: "${fileName}") {
    _id
    fileName
    fileType
    fileSize
    fileUrl
    bucketKey
    status
    errorMessage
    jobSeekerId
    jobSeekerName
    createdAt
    updatedAt
  }
}`;


export const createJobSeekerEmbedding = (jobSeekerId: string) => {
  return `mutation createJobSeekerEmbedding {
    createJobSeekerEmbedding(jobSeekerId: "${jobSeekerId}") {
      _id
    }
  }`;
}

export const createJobSeekerNote = (jobSeekerId: string, note: string) => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateJobSeekerById(
      _id: "${jobSeekerId}",
      jobSeeker: { note: ${JSON.stringify(note)}, updatedBy: "${loginDetails.email ?? ''}" }
    ) {
      _id,
      notes { id, note, date, changedBy }
    }
  }`;
}

export const deleteJobSeekerNote = (jobSeekerId: string, noteId: string) => {
  return `mutation {
    deleteJobSeekerNote(jobSeekerId: "${jobSeekerId}", noteId: "${noteId}") {
      _id,
      notes { id, note, date, changedBy }
    }
  }`;
}

export const createCompanyNote = (companyId: string, note: string) => {
  const loginDetails = currentUserDetails();
  return `mutation {
    updateCompanyById(
      _id: "${companyId}",
      company: { note: ${JSON.stringify(note)}, updatedBy: "${loginDetails.email ?? ''}" }
    ) {
      _id,
      notes { id, note, date, changedBy }
    }
  }`;
}

export const deleteCompanyNote = (companyId: string, noteId: string) => {
  return `mutation {
    deleteCompanyNote(companyId: "${companyId}", noteId: "${noteId}") {
      _id,
      notes { id, note, date, changedBy }
    }
  }`;
}

export const createJobEmbedding = (jobId: string) => {
  return `mutation createJobEmbedding {
    createJobEmbedding(jobId: "${jobId}") {
      _id
    }
  }`;
}

export const getActiveValuesByTypeQuery = (type: string) => {
  return `query { getActiveValuesByType(type: "${type}") }`;
};
// Managed hiring: an internal HR/admin submits a candidate to a client's role on their behalf.
// The backend allows this for internal callers (admin bypass); status defaults to the canonical
// "Submitted" and the application then flows through the normal pipeline/board.
/**
 * Inbound managed-hiring leads (consultation + "request premium" submissions). Admin-only
 * on the backend. Optional status filter; the inbox filters source client-side.
 */
export const getLeadsQuery = (status?: string) => `
  query {
    getLeads${status ? `(status: ${JSON.stringify(status)})` : ''} {
      _id
      company
      contactPerson
      city
      workEmail
      phone
      roles
      approxOpenings
      timeline
      status
      employerId
      source
      createdAt
    }
  }
`;

/**
 * The jobs one premium account holds, for the per-job HR assignment table.
 *
 * Scoped to the employer, not the company, and that is the deliberate answer to a question this
 * screen has been asked twice.
 *
 * It briefly read the whole company instead, because a client with a page of live listings offered
 * only two of them here -- ownership on Mintly is `Job.employerId` and Mintly posts on a client's
 * behalf long before they sign up, so most jobs carry none (7,007 of 7,197 in production). But a
 * company is not one business: a jewellery group runs multiple franchises, each its own employer
 * account with its own plan, and only one of them may subscribe to Managed hiring. Reading the
 * company put another franchise's roles -- and an LTD account's paid-for listings -- in front of an
 * admin about to hand them to a Mintly recruiter.
 *
 * So the narrow scope is correct and the missing jobs are a different problem with its own screen:
 * unowned listings are claimed for an account in the employer workspace's Jobs tab, and once
 * claimed they appear here. `countUnownedCompanyJobs` is what tells the operator that is why the
 * list looks short, rather than leaving them to guess as they did the first time.
 */
export const employerJobsForAssignment = (employerId: string) => `
  query {
    getAllJobs(page: 0, size: 100, sort: "createdAt", sortDirection: "desc", filters: [{ key: "employerId", value: ${JSON.stringify(employerId)} }]) {
      content {
        _id
        title
        jobType
        hiringMode
        jobStatus
        assignedHrId
      }
    }
  }
`;

/** Assign (or unassign, with hrUserId null) the dedicated HR for a job. Super-Admin only. */
/**
 * Set one job's hiring mode: MANAGED (worked by our recruiters for a premium client) or
 * SELF_SERVE (worked by the employer).
 *
 * Its own mutation rather than a field on `updateJobDataById`, for two reasons. That mutation
 * sends every field on the form, so a job whose hiringMode is still null would have an empty
 * string written to it by any unrelated section save. And this is the field the Managed pipeline
 * keys off -- `/hr` filters the job list to MANAGED, and the application access rule reads it --
 * so a deliberate write is easier to reason about than a side effect of saving a job title.
 *
 * `updatedBy` rides along because the generic update stamps it and this one otherwise would not,
 * which would leave the record audit claiming the last editor was whoever touched it before.
 */
export const setJobHiringModeMutation = (jobId: string, hiringMode: string) => {
  const loginDetails = currentUserDetails();
  return `
  mutation {
    updateJobById(_id: ${JSON.stringify(jobId)}, job: {
      hiringMode: ${JSON.stringify(hiringMode)}
      updatedBy: ${JSON.stringify(loginDetails.email ?? '')}
    }) {
      _id
      hiringMode
    }
  }
`;
};

/**
 * Names for a batch of createdBy / updatedBy identifiers.
 *
 * One call per screen rather than one per row: the same author usually wrote every record on the
 * page. Unresolvable ids come back absent, not empty, so the caller keeps its own fallback.
 */
export const resolveActorsQuery = (ids: string[]) => `
  query {
    resolveActors(ids: ${JSON.stringify(ids)}) {
      id
      name
      email
      role
    }
  }
`;

export const assignJobHrMutation = (jobId: string, hrUserId: string | null) => `
  mutation {
    assignJobHr(jobId: ${JSON.stringify(jobId)}, hrUserId: ${hrUserId ? JSON.stringify(hrUserId) : 'null'}) {
      _id
      assignedHrId
    }
  }
`;

/**
 * Lightweight job list for the "submit candidate to a demand" picker — minimal fields,
 * newest first. The client filters by title and surfaces MANAGED demands first.
 */
export const jobsForPicker = () => `
  query {
    getAllJobs(page: 0, size: 100, sort: "createdAt", sortDirection: "desc", filters: []) {
      content {
        _id
        title
        companyName
        city
        hiringMode
      }
    }
  }
`;

/**
 * Ask a candidate for permission to submit them, and move the application to Awaiting consent.
 *
 * Separate from createJobApplication because the two are different acts: the row records that we
 * intend to put this person forward, and this records that we asked them. Creating the row without
 * asking is the state an override produces, and that has to remain expressible.
 */
export const requestRightToRepresentMutation = (applicationId: string) => `
  mutation {
    requestRightToRepresent(applicationId: "${applicationId}") {
      _id
      status
      rtrRequestedAt
    }
  }
`;

/**
 * Submit a candidate whose consent was given somewhere this system cannot see.
 *
 * Super Admin only, enforced by the backend interceptor rather than by hiding the button — the UI
 * decides what is offered, the server decides what is allowed. Always attributed: an override that
 * cannot be traced to a person is not an audit trail.
 */
export const overrideRightToRepresentMutation = (
  applicationId: string,
  overriddenBy: string,
) => `
  mutation {
    overrideRightToRepresent(applicationId: "${applicationId}", overriddenBy: ${JSON.stringify(overriddenBy)}) {
      _id
      status
      rtrOverriddenBy
      rtrOverriddenAt
    }
  }
`;

export const createJobApplicationMutation = (
  jobSeekerId: string,
  jobId: string,
  createdBy: string,
  // A recruiter's candidate starts at Sourcing; the consent flow creates at Qualified.
  status: string = 'Sourcing',
) => `
  mutation {
    createJobApplication(jobApplication: {
      jobSeekerId: "${jobSeekerId}",
      jobId: "${jobId}",
      status: ${JSON.stringify(status)},
      createdBy: "${createdBy}"
    }) {
      _id
      status
    }
  }
`;

// ── Analytics dashboard (Phase 1: Hiring Funnel + Pipeline Health) ────────────
// Named operations so the backend's OperationAuthInterceptor backstop engages in
// addition to the BFF role forwarding. The resolver scopes an HR to their own
// clients; a Super Admin sees platform-wide data.

export interface AnalyticsFilter {
  from?: string;
  to?: string;
  granularity?: 'day' | 'week' | 'month' | 'quarter';
  employerId?: string;
  industry?: string;
  jobType?: string;
  experience?: string;
  city?: string;
  /** One job. */
  jobId?: string;
  /** The recruiter who owns the candidate records; a Super Admin can move one. */
  ownerId?: string;
  /** The job's category. */
  category?: string;
  segmentBy?: 'industry' | 'experience' | 'city';
  currency?: string;
}

/** Build the GraphQL `AnalyticsFilterInput` object literal, omitting blank fields. */
const buildAnalyticsInput = (f: AnalyticsFilter = {}): string => {
  const parts: string[] = [];
  const str = (k: string, v?: string) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      parts.push(`${k}: ${JSON.stringify(String(v))}`);
    }
  };
  str('from', f.from);
  str('to', f.to);
  str('granularity', f.granularity);
  str('employerId', f.employerId);
  str('industry', f.industry);
  str('jobType', f.jobType);
  str('experience', f.experience);
  str('city', f.city);
  str('jobId', f.jobId);
  str('ownerId', f.ownerId);
  str('category', f.category);
  str('segmentBy', f.segmentBy);
  str('currency', f.currency);
  return `{ ${parts.join(', ')} }`;
};

/**
 * Submissions per recruiter per calendar day.
 *
 * Always daily; the backend ignores `granularity` for this query and clamps the window to 92
 * days, returning the clamped `from` so the header can show what was actually rendered.
 */
export const getSubmissionActivityQuery = (f: AnalyticsFilter = {}) => `
  query {
    getSubmissionActivity(input: ${buildAnalyticsInput(f)}) {
      from
      to
      days
      totalSubmissions
      unattributedSubmissions
      scope
      rows {
        recruiterId
        total
        byDay { day count }
      }
    }
  }
`;

/** The candidates behind one cell. `recruiterId` is ignored for an HR caller, who only ever sees their own. */
export const getSubmissionDetailsQuery = (
  f: AnalyticsFilter = {},
  recruiterId?: string,
  day?: string,
) => `
  query {
    getSubmissionDetails(
      input: ${buildAnalyticsInput(f)}
      ${recruiterId ? `recruiterId: ${JSON.stringify(recruiterId)}` : ''}
      ${day ? `day: ${JSON.stringify(day)}` : ''}
    ) {
      applicationId
      jobSeekerId
      candidateName
      jobId
      jobTitle
      companyName
      status
      submittedAt
      submittedBy
      resubmission
    }
  }
`;

export const getHiringFunnelQuery = (f: AnalyticsFilter = {}) => `
  query GetHiringFunnel {
    getHiringFunnel(input: ${buildAnalyticsInput(f)}) {
      jobsPosted
      applicants
      sourced
      contacted
      interested
      qualified
      submitted
      contactRate
      interestRate
      qualificationRate
      submissionToInterviewRate
      placementRate
      avgDaysToFirstContact
      avgDaysToSubmit
      avgDaysToFill
      shortlisted
      interviews
      offers
      accepted
      joined
      applyToShortlistRate
      shortlistToInterviewRate
      interviewToOfferRate
      offerToAcceptRate
      offerToJoinRate
      series { period applicants joined }
      scope
    }
  }
`;

export const getPipelineHealthQuery = (f: AnalyticsFilter = {}) => `
  query GetPipelineHealth {
    getPipelineHealth(input: ${buildAnalyticsInput(f)}) {
      openJobs
      filledJobs
      jobsWithZeroApplicants
      rolesOpenOver30Days
      stuckCandidates
      stageCounts { stage count }
      scope
    }
  }
`;

export const getRecruiterProductivityQuery = (f: AnalyticsFilter = {}) => `
  query GetRecruiterProductivity {
    getRecruiterProductivity(input: ${buildAnalyticsInput(f)}) {
      recruiterId
      jobsHandled
      candidatesHandled
      interviews
      offersMade
      hires
      creditedHires
      avgDaysToShortlist
      avgDaysToHire
      offerToJoinRate
    }
  }
`;

export const getRevenueReportQuery = (f: AnalyticsFilter = {}) => `
  query GetRevenueReport {
    getRevenueReport(input: ${buildAnalyticsInput(f)}) {
      currency
      totalRevenue
      transactions
      payingEmployers
      totalEmployers
      freeToPaidRate
      availableCurrencies
      scope
      byPlan { planType revenue count }
      trend { period revenue }
      topEmployers { employerId companyName revenue }
      planRevenue
      placementRevenue
      placements
      byHr { hrUserId planRevenue placementRevenue placements total }
    }
  }
`;

export const getCandidateQualityQuery = (f: AnalyticsFilter = {}) => `
  query GetCandidateQuality {
    getCandidateQuality(input: ${buildAnalyticsInput(f)}) {
      totalCandidates
      interviewed
      hired
      interviewToHireRate
      evaluatedCandidates
      strongMatchCandidates
      avgMatchScore
      interviewsRecorded
      interviewNoShowRate
      segmentBy
      scope
      segments {
        segment
        candidates
        interviews
        hires
        interviewToHireRate
        shortlistRate
      }
      rejectionReasons { reason count }
    }
  }
`;

export const getJobPerformanceQuery = (f: AnalyticsFilter = {}) => `
  query GetJobPerformance {
    getJobPerformance(input: ${buildAnalyticsInput(f)}) {
      totalViews
      totalApplications
      avgApplyRate
      avgDaysToFirstApplicant
      jobsCovered
      scope
      jobs {
        jobId
        title
        companyName
        views
        applications
        applyRate
        shortlistRate
        dropOffRate
        hires
        daysToFirstApplicant
      }
    }
  }
`;

/**
 * Record the account review decision for an employer. Super-Admin-only on the backend.
 *
 * `status` is PENDING | APPROVED | DECLINED. A reason is required when declining: the backend
 * rejects a decline without one, and the employer is shown the text, so it has to say what they
 * can act on.
 *
 * The reason is free text typed by an admin, and every builder in this file interpolates
 * straight into a GraphQL document. `JSON.stringify` is doing real work here, not tidiness: it
 * emits the surrounding quotes and escapes the quotes, backslashes and newlines that would
 * otherwise terminate the string early and break the query.
 */
export const setEmployerApproval = (
  employerId: string,
  status: 'PENDING' | 'APPROVED' | 'DECLINED',
  reason?: string,
) => `
  mutation {
    setEmployerApproval(
      employerId: ${JSON.stringify(employerId)},
      status: ${JSON.stringify(status)}
      ${reason ? `, reason: ${JSON.stringify(reason)}` : ''}
    ) {
      _id
      approvalStatus
      approvalReason
      approvalDecidedAt
    }
  }
`;

/**
 * Cancel an employer's subscription. Super Admin only (BFF and backend).
 *
 * The employer starts from scratch: no plan, no credits, and their active purchases are marked
 * CANCELLED, so the one-time Pay Per Job purchase can be bought again. The reason is required and
 * the actor is taken from the session server-side, never sent. `closeLiveJobs` also closes their
 * open jobs; it is asked every time rather than defaulted, because the right answer depends on why
 * the plan is being cancelled.
 */
export const cancelEmployerSubscription = (
  employerId: string,
  reason: string,
  closeLiveJobs: boolean,
) => `
  mutation {
    cancelEmployerSubscription(
      employerId: ${gql(employerId)},
      reason: ${gql(reason)},
      closeLiveJobs: ${closeLiveJobs ? 'true' : 'false'}
    ) {
      _id
      planType
      jobsLeft
      planCancelledAt
      planCancelledBy
      planCancelReason
    }
  }
`;

/**
 * Record a refund made by hand in the Razorpay or Stripe dashboard. Super Admin only (BFF and
 * backend). No gateway is called: this writes what already happened, then cancels the employer's
 * plan exactly as `cancelEmployerSubscription` does.
 *
 * `amount` travels as a string because the schema's is one (the backend parses it as a BigDecimal
 * and checks it against the purchase), so a float never rounds a paise away on the way.
 */
export const markSubscriptionRefunded = (
  subscriptionId: string,
  amount: string,
  reference: string,
  reason: string,
  closeLiveJobs: boolean,
) => `
  mutation {
    markSubscriptionRefunded(
      subscriptionId: ${gql(subscriptionId)},
      amount: ${gql(amount)},
      reference: ${gql(reference)},
      ${reason ? `reason: ${gql(reason)},` : ''}
      closeLiveJobs: ${closeLiveJobs ? 'true' : 'false'}
    ) {
      _id
      status
      refundedAt
      refundAmount
      refundReference
    }
  }
`;

/**
 * Draft a meta description with AI, without saving it.
 *
 * Returns the text for the operator to review and save. Deliberately does not persist: clicking
 * Generate is a request for a suggestion, not a decision to keep it, and a mutation that wrote
 * straight through would let a stray click replace copy somebody wrote by hand.
 *
 * Null when generation is disabled or the model's answer fails the backend's length and style
 * checks, in which case whatever is already stored stands.
 */
export const generateJobSeoDescription = (jobId: string) => `mutation {
  generateJobSeoDescription(jobId: ${JSON.stringify(jobId)})
}`;

export const generateCompanySeoDescription = (companyId: string) => `mutation {
  generateCompanySeoDescription(companyId: ${JSON.stringify(companyId)})
}`;

/**
 * How many of a company's jobs have no owner, and the mutation that hands them over.
 *
 * Mintly posts on an employer's behalf before they sign up, so a company being claimed usually
 * has listings that belong to no account at all. `transferJobsToEmployer` cannot help there --
 * it moves jobs BETWEEN employers and needs a source. These have none.
 *
 * The assign mutation is exposed on its own as well as inside approval, because a request that
 * is already approved cannot be approved again, and every company claimed before this existed
 * is in exactly that state.
 */
export const countUnownedCompanyJobs = (companyId: string) => `
  query {
    countUnownedCompanyJobs(companyId: "${companyId}")
  }
`;

export const assignUnownedCompanyJobs = (companyId: string, employerId: string) => `
  mutation {
    assignUnownedCompanyJobs(companyId: "${companyId}", employerId: "${employerId}")
  }
`;

/**
 * Every job on a company, with its current owner, for the assignment picker.
 *
 * `getAllJobs` already takes key/value equality filters and JobsData exposes employerId, so this
 * needs no new backend query. The owner id is resolved to an email in the component from the
 * company's attached employers; a job owned by an account not attached to this company will not
 * resolve and is shown as "another account" rather than as unassigned, which is the one wrong
 * answer -- it would invite an admin to take a live listing believing it belonged to nobody.
 */
/**
 * Application counts for one company's jobs, in a single round trip.
 *
 * The job picker can move a listing between employers, and moving one takes its applicants with
 * it. Ownership was the only fact on the row, so an admin could hand away the job carrying most
 * of the pipeline and the screen would not have mentioned it. This is the second most useful
 * thing to know before ticking a box.
 *
 * One query, not one per row. `job.companyId` is a nested filter applied after the lookup, the
 * same mechanism `job.employerId` already uses. The rows come back carrying only `jobId` and are
 * tallied on the client, because there is no per-job application count in the schema and adding
 * one is a backend change this does not need: production holds 10,567 applications in total and
 * the busiest single job has 62, so a company's whole set fits comfortably in one page.
 *
 * `totalElements` is selected so the caller can tell a complete tally from a truncated one rather
 * than quietly under-reporting.
 */
export const companyApplicationCounts = (companyId: string) => `
  query {
    getAllJobApplications(page: 0, size: 500, filters: [{ key: "job.companyId", value: ${JSON.stringify(companyId)} }]) {
      totalElements
      content {
        jobId
      }
    }
  }
`;

export const companyJobsForAssignment = (companyId: string) => `
  query {
    getAllJobs(page: 0, size: 200, sort: "postedDate", sortDirection: "desc", filters: [{ key: "companyId", value: ${JSON.stringify(companyId)} }]) {
      totalElements
      content {
        _id
        title
        jobStatus
        postedDate
        employerId
      }
    }
  }
`;

/**
 * Set exactly which of a company's jobs belong to an employer.
 *
 * The picker's confirm. Ticked jobs become theirs; any job on this company that is currently
 * theirs and is left unticked goes back to unowned, which is what makes an over-assignment
 * correctable from the same screen that caused it. Jobs owned by somebody else are never released
 * by omission -- taking one stays an explicit tick.
 */
export const setEmployerJobsOnCompany = (
  companyId: string,
  employerId: string,
  jobIds: string[],
) => `
  mutation {
    setEmployerJobsOnCompany(companyId: "${companyId}", employerId: "${employerId}", jobIds: ${JSON.stringify(jobIds)})
  }
`;

/**
 * Assign the chosen jobs, additively. Kept for callers that only ever add.
 */
export const assignJobsToEmployer = (
  jobIds: string[],
  employerId: string,
  companyId: string,
) => `
  mutation {
    assignJobsToEmployer(jobIds: ${JSON.stringify(jobIds)}, employerId: "${employerId}", companyId: "${companyId}")
  }
`;

/** How many jobs a company has at all. Drives whether the assignment card is worth showing. */
export const companyJobsCount = (companyId: string) => `
  query {
    getAllJobs(page: 0, size: 1, filters: [{ key: "companyId", value: ${JSON.stringify(companyId)} }]) {
      totalElements
    }
  }
`;

/**
 * Fill empty structured fields on existing candidates from the CV already on their record.
 * Super Admin only. `dryRun` true reports and writes nothing; the backend caps `limit` at 100.
 */
export const backfillJobSeekerProfilesMutation = (dryRun: boolean, limit: number) => `
  mutation {
    backfillJobSeekerProfiles(dryRun: ${dryRun ? 'true' : 'false'}, limit: ${Math.trunc(limit)}) {
      dryRun
      scanned
      updated
      unchanged
      failed
      remaining
      fieldsFilled { key value }
      failures { key value }
    }
  }
`;

/**
 * Candidates in a city: living in it or one of its configured areas, or preferring it. Same rule
 * as the job-seeker list's City filter, so the two totals agree.
 */
/**
 * `range` counts only candidates created in it (yyyy-MM-dd, either end optional). Omitted, the
 * query is exactly the all-time one, so a backend without the arguments still answers it.
 */
export const candidateCityBreakdownQuery = (city: string, range: { from?: string; to?: string } = {}) => `
  query {
    candidateCityBreakdown(city: ${JSON.stringify(city)}${range.from ? `, from: ${JSON.stringify(range.from)}` : ''}${range.to ? `, to: ${JSON.stringify(range.to)}` : ''}) {
      city
      total
      livesThere
      prefersOnly
      external
      internal
      byArea { key value }
      byExperience { key value }
      byRole { key value }
      byPreferredLocality { key value }
      bySkill { key value }
    }
  }
`;

const PLACEMENT_FEE_FIELDS = `
      _id applicationId jobId jobTitle employerId jobSeekerId hrUserId
      basis monthlySalary percent candidateCtc amount currency hiredAt notes
      createdAt createdBy updatedAt updatedBy`;

/** Recent placement fees, newest first. The backend scopes an HR to their own. */
export const getPlacementFeesQuery = () => `
  query {
    getPlacementFees {${PLACEMENT_FEE_FIELDS}
    }
  }
`;

export const getPlacementFeeByApplicationQuery = (applicationId: string) => `
  query {
    getPlacementFeeByApplication(applicationId: ${gql(applicationId)}) {${PLACEMENT_FEE_FIELDS}
    }
  }
`;

/**
 * How managed hiring is paid: when a placed candidate joins, the client pays one month of their
 * salary (ONE_MONTH) or half a month (HALF_MONTH). The backend computes the amount.
 */
export type PlacementFeeBasis = 'ONE_MONTH' | 'HALF_MONTH';

export interface PlacementFeeInput {
  applicationId?: string;
  basis: PlacementFeeBasis;
  /** The candidate's monthly salary at joining. */
  monthlySalary: number | null;
  currency: string;
  hrUserId?: string | null;
  hiredAt?: string | null;
  notes?: string | null;
}

const placementFeeLiteral = (f: PlacementFeeInput) => `{
      applicationId: ${f.applicationId ? gql(f.applicationId) : 'null'}
      basis: ${gql(f.basis)}
      monthlySalary: ${f.monthlySalary == null ? 'null' : Number(f.monthlySalary)}
      currency: ${gql(f.currency)}
      hrUserId: ${f.hrUserId ? gql(f.hrUserId) : 'null'}
      hiredAt: ${f.hiredAt ? gql(f.hiredAt) : 'null'}
      notes: ${f.notes ? gql(f.notes) : 'null'}
    }`;

/** Super Admin only (BFF and backend both refuse anyone else). */
export const createPlacementFeeMutation = (f: PlacementFeeInput) => `
  mutation {
    createPlacementFee(fee: ${placementFeeLiteral(f)}) {${PLACEMENT_FEE_FIELDS}
    }
  }
`;

export const updatePlacementFeeMutation = (id: string, f: PlacementFeeInput) => `
  mutation {
    updatePlacementFee(_id: ${gql(id)}, fee: ${placementFeeLiteral(f)}) {${PLACEMENT_FEE_FIELDS}
    }
  }
`;

export const deletePlacementFeeMutation = (id: string) => `
  mutation {
    deletePlacementFee(_id: ${gql(id)})
  }
`;

// ── Activity monitor (Super Admin only; BFF and backend both refuse anyone else) ──────────────

export interface ActivityFilterInput {
  actorType?: string;
  actorId?: string;
  action?: string;
  from?: string;
  to?: string;
}

const activityFilterLiteral = (f: ActivityFilterInput) =>
  `{ ${(['actorType', 'actorId', 'action', 'from', 'to'] as const)
    .filter((k) => f[k])
    .map((k) => `${k}: ${gql(f[k])}`)
    .join(' ')} }`;

export const getActivityQuery = (filter: ActivityFilterInput, page: number, size: number) => `
  query {
    getActivity(filter: ${activityFilterLiteral(filter)}, page: ${Number(page)}, size: ${Number(size)}) {
      total
      items { id actorId actorType actorRole actorName actorEmail action entityType entityId timestamp }
    }
  }
`;

export const getActivitySummaryQuery = (days: number) => `
  query {
    getActivitySummary(days: ${Number(days)}) { day jobSeekers employers staff events }
  }
`;

/** Control Center sign-in attempts (LOGIN and LOGIN_FAILED), newest first. Super Admin only. */
export const getSignInAttemptsQuery = (failedOnly: boolean, page: number, size: number) => `
  query {
    getSignInAttempts(failedOnly: ${failedOnly ? 'true' : 'false'}, page: ${Number(page)}, size: ${Number(size)}) {
      total
      items { id actorId actorType actorRole actorName actorEmail action reason ip userAgent timestamp }
    }
  }
`;

export const getSignInSummaryQuery = () => `
  query {
    getSignInSummary { failed24h succeeded24h failed7d succeeded7d }
  }
`;

export const getActivityActionsQuery = () => `
  query {
    getActivityActions
  }
`;

/** Self sign-ups per IST day and by channel. Super Admin only (BFF and backend). */
export const getSignupStatsQuery = (days: number) => `
  query {
    getSignupStats(days: ${Number(days)}) {
      jobSeekers
      employers
      days { day jobSeekers employers }
      channels { key jobSeekers employers }
      methods { key jobSeekers employers }
    }
  }
`;

/**
 * Self sign-ups per IST week (Monday start), by channel, landing page group and method. Oldest
 * week first; the last is the current, partial one. Super Admin only (BFF and backend).
 */
export const getSignupAttributionQuery = (weeks: number) => `
  query {
    getSignupAttribution(weeks: ${Number(weeks)}) {
      weeks {
        weekStart
        jobSeekers
        employers
        channels { key jobSeekers employers }
        landingPages { key jobSeekers employers }
        methods { key jobSeekers employers }
      }
    }
  }
`;

// ── Platform settings (Super Admin only; BFF and backend both refuse anyone else) ────────────

export const getPlatformSettingsQuery = () => `
  query {
    getPlatformSettings { key value updatedAt updatedBy }
  }
`;

export const setPlatformSettingMutation = (key: string, value: string) => `
  mutation {
    setPlatformSetting(key: ${gql(key)}, value: ${gql(value)}) { key value updatedAt updatedBy }
  }
`;

// ── Referrals ────────────────────────────────────────────────────────────────────────────────

/** The signed-in HR's own referral code and who joined through it. */
export const myReferralsQuery = () => `
  query {
    myReferrals {
      code amountPerReferral currency eligible referred qualified earned paid
      underReview pendingAmount phoneVerificationRequired
      people { name joinedAt qualified eligible status statusReason }
      payoutUpiId payoutPhone
    }
  }
`;

/** Narrows the payouts list. The totals the query returns stay programme-wide whatever is set. */
export interface ReferralRewardFilters {
  /** JOBSEEKER, HR or AMBASSADOR; empty for every kind. */
  referrerType?: string;
  /** One referrer's id. */
  referrerId?: string;
  /** A referrer's name, email or referral code. */
  search?: string;
}

/** Super Admin only (BFF and backend). A blank filter is left out rather than sent as "". */
export const getReferralRewardsQuery = (
  status: string,
  page: number,
  size: number,
  filters: ReferralRewardFilters = {},
) => {
  const extra = (['referrerType', 'referrerId', 'search'] as const)
    .map((k) => [k, filters[k]?.trim() ?? ''] as const)
    .filter(([, v]) => v)
    .map(([k, v]) => `, ${k}: ${gql(v)}`)
    .join('');
  return `
  query {
    getReferralRewards(status: ${gql(status)}, page: ${Number(page)}, size: ${Number(size)}${extra}) {
      total
      outstanding
      pendingReview pendingReviewCount
      items {
        id referrerType referrerId referrerName referrerEmail
        referredId referredName
        amount currency status earnedAt paidAt paidBy paymentRef
        payoutUpiId payoutPhone
        rejectedReason reviewedBy reviewedAt
        checks { name passed }
        relevance { verdict matchedTerms negativeTerms industries score cvTextChars }
        flags {
          referredLast24h referredLast7d payoutUpiSharedWith signupIpHash sameIpReferred
          minutesSincePreviousReferral
        }
        completenessAtQualification resumeChars
        kind creditId threshold tierAmount legacyNetted
        contributors { creditId referredId name excluded }
        applicationId drawId month
      }
    }
  }
`;
};

/** Super Admin only (BFF and backend): take one referred person out of milestone counting. */
export const excludeReferralCreditMutation = (creditId: string, reason: string) => `
  mutation {
    excludeReferralCredit(creditId: ${gql(creditId)}, reason: ${gql(reason)})
  }
`;

/** Super Admin only (BFF and backend): count a previously excluded person again. */
export const restoreReferralCreditMutation = (creditId: string) => `
  mutation {
    restoreReferralCredit(creditId: ${gql(creditId)})
  }
`;

const DRAW_FIELDS = `
  id month totalEntries seed pickHash pickIndex winnerId winnerName prize currency rewardId runBy runAt
  entries { referrerId referrerName entries }
`;

/** Super Admin only (BFF and backend): every monthly draw run, newest first. */
export const referralDrawsQuery = () => `
  query {
    referralDraws { ${DRAW_FIELDS} }
  }
`;

/** Super Admin only (BFF and backend): who is in a month's draw so far (yyyy-MM). */
export const referralDrawEntriesQuery = (month: string) => `
  query {
    referralDrawEntries(month: ${gql(month)}) { referrerId referrerName entries }
  }
`;

/** Super Admin only (BFF and backend): run a finished month's draw. Once per month. */
export const runReferralDrawMutation = (month: string) => `
  mutation {
    runReferralDraw(month: ${gql(month)}) { ${DRAW_FIELDS} }
  }
`;

/** Super Admin only (BFF and backend): every referred job seeker with who referred them. */
export const getReferredJobSeekersQuery = (
  referrerType: string,
  qualified: '' | 'true' | 'false',
  search: string,
  page: number,
  size: number,
) => `
  query {
    getReferredJobSeekers(
      referrerType: ${gql(referrerType)}
      ${qualified ? `qualified: ${qualified === 'true'}` : ''}
      search: ${gql(search)}
      page: ${Number(page)}
      size: ${Number(size)}
    ) {
      total
      items {
        jobSeekerId name email joinedAt qualified qualifiedAt code
        referrerType referrerId referrerName referrerEmail
        pendingReason statusReason
        checks { name passed }
      }
    }
  }
`;

/**
 * A job seeker's name and email, and nothing else. The referral workspace has no per-person
 * referral query to call, so it finds the person's row in getReferredJobSeekers by searching for
 * their email; this is how it learns the email from the id in the URL.
 */
export const getJobSeekerIdentityQuery = (id: string) => `
  query {
    getJobSeekerById(_id: ${gql(id)}) {
      _id
      personalDetails { fullName firstName lastName email }
    }
  }
`;

/** Super Admin only (BFF and backend): programme-wide referral metrics for the dashboard. */
export const getReferralMetricsQuery = () => `
  query {
    getReferralMetrics {
      totalReferred totalQualified qualifiedRate
      totalEarned totalPaid outstanding rewardsEarned currency
      rewardsPendingReview rewardsRejected pendingReview
      byReferrerType { referrerType referred qualified }
      qualifiedOverTime { month qualified }
      topReferrers { referrerType referrerId referrerName referrerEmail qualified earned }
    }
  }
`;

/** The caller's own payout details (HR or Super Admin in the CC). Blank clears a field. */
export const setMyReferralPayoutMutation = (upiId: string, phone: string) => `
  mutation {
    setMyReferralPayout(upiId: ${gql(upiId)}, phone: ${gql(phone)}) { payoutUpiId payoutPhone }
  }
`;

/** Super Admin only (BFF and backend). */
export const markReferralRewardsPaidMutation = (ids: string[], paymentRef: string) => `
  mutation {
    markReferralRewardsPaid(ids: ${JSON.stringify(ids)}, paymentRef: ${gql(paymentRef)})
  }
`;

/** Super Admin only (BFF and backend): PENDING_REVIEW becomes EARNED, payable. Returns the count. */
export const approveReferralRewardMutation = (ids: string[]) => `
  mutation {
    approveReferralReward(ids: ${JSON.stringify(ids)})
  }
`;

/** Super Admin only (BFF and backend). The reason is required and is shown to the referrer. */
export const rejectReferralRewardMutation = (ids: string[], reason: string) => `
  mutation {
    rejectReferralReward(ids: ${JSON.stringify(ids)}, reason: ${gql(reason)})
  }
`;

// ── HR Ambassadors (referral programme v3) ───────────────────────────────────────────────────
// Every operation below is Super Admin only, in the BFF blocklist and in the backend.

const AMBASSADOR_FIELDS = `
  _id authProvider firm status statusReason code codeRenamedAt createdAt lastLoginAt
  personalDetails { firstName lastName email phone city }
`;

/** Ambassadors, newest first. Status ACTIVE or SUSPENDED; search matches name, email, city and firm. */
export const getAmbassadorsQuery = (status: string, search: string, page: number, size: number) => `
  query {
    getAmbassadors(
      ${status ? `status: ${gql(status)}` : ''}
      ${search ? `search: ${gql(search)}` : ''}
      page: ${Number(page)}
      size: ${Number(size)}
    ) {
      total
      items { ${AMBASSADOR_FIELDS} }
    }
  }
`;

export const getAmbassadorByIdQuery = (id: string) => `
  query {
    getAmbassadorById(_id: ${gql(id)}) { ${AMBASSADOR_FIELDS} }
  }
`;

/** ACTIVE or SUSPENDED. The backend refuses a suspension without a reason. */
export const setAmbassadorStatusMutation = (id: string, status: string, reason: string) => `
  mutation {
    setAmbassadorStatus(_id: ${gql(id)}, status: ${gql(status)}${reason ? `, reason: ${gql(reason)}` : ''}) {
      ${AMBASSADOR_FIELDS}
    }
  }
`;

/** Numbers, milestones, funnel and payout details, as the ambassador's own dashboard sees them. */
export const adminAmbassadorSummaryQuery = (id: string) => `
  query {
    adminAmbassadorSummary(_id: ${gql(id)}) {
      code currency counted qualified earned paid pendingAmount underReview
      badge nextBadge { badge at }
      tiers { threshold amount reached owed status }
      funnel { invited registered profileCompleted applied interviewing hired qualified }
      payoutUpiId payoutPhone
    }
  }
`;

/** One tab of the people they referred: all, needs, verified, interviewing or placed. */
export const adminAmbassadorPeopleQuery = (id: string, tab: string, page: number, size: number) => `
  query {
    adminAmbassadorPeople(_id: ${gql(id)}, tab: ${gql(tab)}, page: ${Number(page)}, size: ${Number(size)}) {
      total all needs verified interviewing placed
      items { name role city stage status statusReason priority joinedAt }
    }
  }
`;

export const adminAmbassadorRewardsQuery = (id: string) => `
  query {
    adminAmbassadorRewards(_id: ${gql(id)}) {
      id kind title amount currency status earnedAt paidAt paymentRef rejectedReason priority
    }
  }
`;

/** Their email invites, newest first. Status SENT, JOINED or CANCELLED; empty for all. */
export const adminAmbassadorInvitesQuery = (id: string, status: string, page: number, size: number) => `
  query {
    adminAmbassadorInvites(
      _id: ${gql(id)}
      ${status ? `status: ${gql(status)}` : ''}
      page: ${Number(page)}
      size: ${Number(size)}
    ) {
      total
      items { _id name email hasResume resumeFileName relevance status sentAt lastSentAt resendCount joinedAt }
    }
  }
`;

export const adminAmbassadorCertificatesQuery = (id: string) => `
  query {
    adminAmbassadorCertificates(_id: ${gql(id)}) { _id level name issuedAt active }
  }
`;

/** One record's history for its workspace History tab (HR and Super Admin). */
export const getRecordActivityQuery = (recordId: string, page: number, size: number) => `
  query {
    getRecordActivity(recordId: ${gql(recordId)}, page: ${Number(page)}, size: ${Number(size)}) {
      total
      items { id actorId actorType actorRole actorName actorEmail action entityType entityId timestamp }
    }
  }
`;

// ── SEO, GEO, AEO tracker ───────────────────────────────────────────────────────────────────
// Super Admin and Marketing (BFF SEO_TRACKER_FIELDS, and the backend's interceptor and SeoController).

const SEO_GOOGLE_INDEX_FIELDS =
  'verdict coverageState indexingState lastCrawlTime googleCanonical inspectedAt';

const SEO_PAGE_FIELDS = `
  path pageType group vertical city locality industry role primaryKeyword secondaryKeywords inSitemap inLlms
  indexable liveJobs lastActivity httpStatus title description h1 canonical robots faqCount
  schemaTypes fetchError status firstSeenAt lastChangedAt lastSyncedAt noteCount
  googleIndex { ${SEO_GOOGLE_INDEX_FIELDS} }
  clicks28 impressions28 ctr28 position28 clicksPrev28 primaryKeywordPosition notIndexed dropping
  sessions28 signups28 applies28 aiSessions28 searchSyncedAt
`;

export interface SeoPageFilterInput {
  q?: string;
  pageType?: string;
  indexable?: boolean;
  inLlms?: boolean;
  city?: string;
  locality?: string;
  industry?: string;
  role?: string;
  status?: string;
  notIndexed?: boolean;
  dropping?: boolean;
}

const seoFilterLiteral = (f: SeoPageFilterInput): string => {
  const parts = Object.entries(f)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'boolean' ? String(v) : gql(v)}`);
  return `{ ${parts.join(', ')} }`;
};

export const seoPagesQuery = (
  filter: SeoPageFilterInput,
  page: number,
  size: number,
  sort?: string,
) => `
  query {
    seoPages(filter: ${seoFilterLiteral(filter)},${sort ? ` sort: ${gql(sort)},` : ''} page: ${Number(page) || 0}, size: ${Number(size) || 50}) {
      totalElements totalPages content { ${SEO_PAGE_FIELDS} }
      totals { pages clicks28 impressions28 sessions28 signups28 applies28 aiSessions28 }
    }
  }
`;

/** The city, locality, industry and role filter values, each with its LIVE page count. */
export const seoFacetsQuery = () => `
  query {
    seoFacets {
      cities { value count }
      localities { value count }
      industries { value count }
      roles { value count }
    }
  }
`;

/**
 * Every LIVE page for one role (the Categories tab), with its keyword checks. Kept apart from
 * seoPagesQuery so the Pages list, which can hold every page, does not carry the checks too.
 */
export const seoRolePagesQuery = (role: string) => `
  query {
    seoPages(filter: ${seoFilterLiteral({ role, status: 'LIVE' })}, page: 0, size: 100) {
      totalElements
      content { ${SEO_PAGE_FIELDS} ${SEO_KEYWORD_CHECK_FIELDS} }
    }
  }
`;

const SEO_SYNC_STATE_FIELDS = 'running startedAt finishedAt error result';

/** Where each target keyword appears on the live page (backend SeoKeywordMatcher). */
const SEO_KEYWORD_CHECK_FIELDS =
  'keywordChecks { keyword primary inTitle inH1 inDescription inH2 inBody onPage }';

export const seoPageQuery = (path: string) => `
  query {
    seoPage(path: ${gql(path)}) { ${SEO_PAGE_FIELDS} ${SEO_KEYWORD_CHECK_FIELDS} }
    seoPageEvents(path: ${gql(path)}, page: 0, size: 200) {
      totalElements content { id path at kind text note by changes { field from to } }
    }
  }
`;

export const seoPageEventsQuery = (kind: string, page: number, size: number) => `
  query {
    seoPageEvents(kind: ${gql(kind)}, page: ${Number(page) || 0}, size: ${Number(size) || 50}) {
      totalElements content { id path at kind text note by changes { field from to } }
    }
  }
`;

export const seoSummaryQuery = () => `
  query {
    seoSummary {
      total indexable inLlms withFaq changedLast7Days lastSyncedAt byType { pageType count }
      clicks28 impressions28 sessions28 signups28 aiSessions28 notIndexed dropping searchSyncedAt
      ga4Connected
      searchSync { ${SEO_SYNC_STATE_FIELDS} }
      inventorySync { ${SEO_SYNC_STATE_FIELDS} }
    }
  }
`;

export const addSeoNoteMutation = (path: string, text: string, byName: string) => `
  mutation {
    addSeoNote(path: ${gql(path)}, text: ${gql(text)}, byName: ${gql(byName)}) {
      id path at kind text note by changes { field from to }
    }
  }
`;

export const syncSeoInventoryMutation = () => `
  mutation {
    syncSeoInventory { ${SEO_SYNC_STATE_FIELDS} }
  }
`;

export interface SeoKeywordFilterInput {
  q?: string;
  path?: string;
  minImpressions?: number;
}

const seoKeywordFilterLiteral = (f: SeoKeywordFilterInput): string => {
  const parts: string[] = [];
  if (f.q) parts.push(`q: ${gql(f.q)}`);
  if (f.path) parts.push(`path: ${gql(f.path)}`);
  if (Number.isFinite(f.minImpressions) && (f.minImpressions ?? 0) > 0)
    parts.push(`minImpressions: ${Math.floor(Number(f.minImpressions))}`);
  return `{ ${parts.join(', ')} }`;
};

export const seoKeywordsQuery = (
  filter: SeoKeywordFilterInput,
  sort: string,
  page: number,
  size: number,
) => `
  query {
    seoKeywords(filter: ${seoKeywordFilterLiteral(filter)}, sort: ${gql(sort || 'clicks')}, page: ${Number(page) || 0}, size: ${Number(size) || 50}) {
      totalElements totalPages from to
      content {
        query clicks impressions ctr position topPage topPageClicks pages clicksPrev positionPrev
        clicksChange positionChange
      }
    }
  }
`;

export const seoPagePerformanceQuery = (path: string, days: number) => `
  query {
    seoPagePerformance(path: ${gql(path)}, days: ${Number(days) || 90}) {
      path from to
      daily { date clicks impressions ctr position sessions signups }
      topQueries { query clicks impressions ctr position }
      channels { organic ai direct social referral other }
      googleIndex { ${SEO_GOOGLE_INDEX_FIELDS} }
    }
  }
`;

export const syncSearchPerformanceMutation = () => `
  mutation {
    syncSearchPerformance { ${SEO_SYNC_STATE_FIELDS} }
  }
`;

/**
 * AI spend by feature from the ai_usage ledger (backend and external jobs), IST days, both bounds
 * inclusive. Super Admin only (BFF and backend).
 */
export const aiUsageSummaryQuery = (from: string, to: string) => `
  query {
    aiUsageSummary(from: ${JSON.stringify(from)}, to: ${JSON.stringify(to)}) {
      from
      to
      totalUsd
      byFeature { feature service model calls tokensIn tokensOut costUsd }
      byDay { date costUsd }
      unpriced
    }
  }
`;

/**
 * Chrome notifications for staff (owner, 2026-10-24). The token is this browser's FCM token; the
 * backend owns it to the SESSION's caller (X-User-Id / admin), never to an argument, so there is
 * no user id to send. `userType: "admin"` is what the notification service looks tokens up by for
 * admin_notifications. Both return scalars.
 */
export const savePushTokenMutation = (token: string, deviceInfo: string) => `
  mutation {
    savePushToken(token: ${gql(token)}, userType: "admin", deviceInfo: ${gql(deviceInfo)})
  }
`;

export const deletePushTokenMutation = (token: string) => `
  mutation {
    deletePushToken(token: ${gql(token)})
  }
`;

/**
 * The staff member's on/off switch for Chrome notifications. Sends `pushEnabled` alone: an omitted
 * `subscribedEvents` leaves the event list as it is on the server, so flipping this switch cannot
 * save the event switches' unsaved edits by accident.
 */
export const updateAdminPushEnabledMutation = (pushEnabled: boolean) => `
  mutation {
    updateAdminNotificationPreferences(pushEnabled: ${pushEnabled ? 'true' : 'false'}) {
      userId subscribedEvents availableEvents pushEnabled
    }
  }
`;

// ── City-expansion promo codes (Super Admin; refused for anyone else by the BFF and the backend) ──

const PROMO_CODE_FIELDS = `
  _id code description targetCities active status expiresAt maxRedemptions redemptionCount
  jobValidityDays createdAt createdBy updatedAt updatedBy
`;

/** A whole number for the schema's Int, or null for "none". The form keeps numbers as strings. */
const intOrNull = (value: string): string => {
  const trimmed = value.trim();
  return /^\d+$/.test(trimmed) ? String(Number(trimmed)) : 'null';
};

/**
 * Every setting, sent whole: updatePromoCode replaces them all, so null here means "none" (no
 * expiry, no limit) rather than "unchanged". The code itself is only sent on create.
 */
const promoSettings = (form: PromoForm): string => `
  description: ${gql(form.description.trim())},
  targetCities: ${JSON.stringify(form.targetCities)},
  active: ${form.active ? 'true' : 'false'},
  expiresAt: ${form.expiresAt ? gql(form.expiresAt) : 'null'},
  maxRedemptions: ${intOrNull(form.maxRedemptions)},
  jobValidityDays: ${intOrNull(form.jobValidityDays)}
`;

export const getPromoCodesQuery = () => `
  query {
    promoCodes { ${PROMO_CODE_FIELDS} }
  }
`;

export const createPromoCodeMutation = (form: PromoForm) => `
  mutation {
    createPromoCode(input: { code: ${gql(form.code.trim().toUpperCase())}, ${promoSettings(form)} }) {
      ${PROMO_CODE_FIELDS}
    }
  }
`;

export const updatePromoCodeMutation = (id: string, form: PromoForm) => `
  mutation {
    updatePromoCode(id: ${gql(id)}, input: { ${promoSettings(form)} }) {
      ${PROMO_CODE_FIELDS}
    }
  }
`;

export const getPromoRedemptionsQuery = (code: string, page: number, size: number) => `
  query {
    promoRedemptions(code: ${gql(code)}, page: ${Math.max(0, Math.floor(page))}, size: ${Math.max(1, Math.floor(size))}) {
      content {
        subscriptionId employerId employerName companyId companyName redeemedAt status postedCity
      }
      totalPages
      totalElements
    }
  }
`;

/**
 * Open jobs per city, in one request, for the promo code city picker. Advice for choosing cities
 * that need jobs, not a rule: the backend's city filter is a case-insensitive substring match, so
 * "Mumbai" also counts "Navi Mumbai". Each count is aliased c0, c1, ... in the order given.
 */
export const getOpenJobCountsByCityQuery = (cities: string[]) => `
  query {
    ${cities.map((city, i) => `c${i}: getJobsCount(filters: [{ key: "city", value: ${gql(city)} }, { key: "jobStatus", value: "OPEN" }])`).join('\n    ')}
  }
`;
